(() => {
  const parseQuantity = (value) => {
    const match = String(value || '').replace(/,/g, '').match(/\d+/);
    return match ? Number(match[0]) : 0;
  };

  // A product opts in through custom.quantity_tier_matrix. The matrix names the
  // quantities allowed for each non-quantity option tuple; missing quotes stay
  // unavailable even if a different configuration offers that quantity.
  const allowedQuantityTiers = (matrix, optionValues, quantityIndex, allQuantities) => {
    if (matrix?.enabled !== true || !Array.isArray(optionValues)
      || quantityIndex < 0 || quantityIndex >= optionValues.length) return null;
    const selected = optionValues.filter((_, index) => index !== quantityIndex);
    const exceptions = Array.isArray(matrix.exceptions) ? matrix.exceptions : [];
    const matching = exceptions.filter((rule) => Array.isArray(rule?.options)
      && rule.options.length === selected.length
      && rule.options.every((value, index) => value === selected[index]));
    if (matching.length > 1) return null;
    const quantities = matching.length ? matching[0].quantities : matrix.default_quantities;
    if (!Array.isArray(quantities)
      || quantities.some((value) => typeof value !== 'string' || !allQuantities.includes(value))
      || new Set(quantities).size !== quantities.length) return null;
    return new Set(quantities);
  };

  // Explicit presentation rows let equal-priced customer choices share a native
  // variant. Products without these rows retain their existing split mapping.
  const configuredPresentationRows = (config, nativeValues, attributeCount, separator) => {
    const explicit = Object.prototype.hasOwnProperty.call(config, 'presentation_combinations');
    const rows = explicit ? config.presentation_combinations : nativeValues.map((native) => ({
      values: native.split(separator), native,
    }));
    if (!Array.isArray(rows) || !rows.length) return [];
    const nativeSet = new Set(nativeValues);
    const seen = new Set();
    for (const row of rows) {
      if (!Array.isArray(row?.values) || row.values.length !== attributeCount
        || row.values.some((value) => typeof value !== 'string' || !value.trim())
        || !nativeSet.has(row.native)) return [];
      const key = JSON.stringify(row.values);
      if (seen.has(key)) return [];
      seen.add(key);
    }
    if (explicit && new Set(rows.map((row) => row.native)).size !== nativeSet.size) return [];
    return rows;
  };

  const formatCurrency = (cents, currency, locale) => {
    if (!Number.isFinite(cents)) return '—';
    try {
      return new Intl.NumberFormat(locale || 'en-US', {
        style: 'currency',
        currency: currency || 'USD',
        currencyDisplay: 'narrowSymbol',
      }).format(cents / 100);
    } catch (_) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  const formatUnitPrice = (batchTotalCents, quantity, currency, locale) => {
    if (!Number.isFinite(batchTotalCents) || !Number.isFinite(quantity) || quantity <= 0) return '—';
    const unitPrice = batchTotalCents / 100 / quantity;
    try {
      return new Intl.NumberFormat(locale || 'en-US', {
        style: 'currency',
        currency: currency || 'USD',
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      }).format(unitPrice);
    } catch (_) {
      return `$${unitPrice.toFixed(3)}`;
    }
  };

  const cup018SizeFromOption = (value) => String(value || '').split(' · ')[1] || '';

  const prepareCup001QuantityRows = (root, rows, locale) => {
    rows.sort((first, second) => parseQuantity(first.dataset.tierValue) - parseQuantity(second.dataset.tierValue));
    const table = rows[0]?.parentElement;
    const otherQuantity = root.querySelector('[data-11p-other-tier]');
    const selectedExtra = rows.some((row, index) => index >= 6 && row.querySelector('input').checked);
    const numberFormat = new Intl.NumberFormat(locale || 'en-US');
    rows.forEach((row, index) => {
      const extra = index >= 6;
      row.classList.toggle('is-extra', extra);
      row.hidden = extra && !selectedExtra;
      const label = row.querySelector('.elevenp-tier-row__quantity strong');
      if (label) label.textContent = numberFormat.format(parseQuantity(row.dataset.tierValue));
      if (table) table.insertBefore(row, otherQuantity);
    });
    if (selectedExtra) {
      const toggle = root.querySelector('[data-11p-tier-toggle]');
      toggle?.setAttribute('aria-expanded', 'true');
      const label = toggle?.querySelector('span');
      if (label) label.textContent = 'Show fewer quantities';
      if (otherQuantity) otherQuantity.hidden = false;
    }
  };

  const isUnsupportedCup002Quantity = (matrixType, size, quantity) => (
    matrixType === 'cup002' && size === '4 oz | 120 ml | 62 mm Rim'
    && ['1000', '2000', '3000', '5000', '8000'].includes(String(quantity))
  );

  // Display only quantities quoted for the currently selected configuration.
  const refreshQuotedQuantityRows = (root, rows) => {
    const quotedRows = rows.filter((row) => row._elevenpVariant);
    const toggle = root.querySelector('[data-11p-tier-toggle]');
    const otherQuantity = root.querySelector('[data-11p-other-tier]');
    const selectedExtra = quotedRows.some((row, index) => (
      index >= 6 && row.querySelector('input').checked && row._elevenpVariant.available
    ));
    const expanded = toggle?.getAttribute('aria-expanded') === 'true' || selectedExtra;
    rows.forEach((row) => {
      const index = quotedRows.indexOf(row);
      const extra = index >= 6;
      row.classList.toggle('is-extra', extra);
      row.hidden = index < 0 || (extra && !expanded);
    });
    if (toggle) {
      toggle.hidden = quotedRows.length === 0 && !otherQuantity;
      toggle.setAttribute('aria-expanded', String(expanded));
      const label = toggle.querySelector('span');
      if (label) label.textContent = expanded ? 'Show fewer quantities' : 'Show more quantities';
    }
    if (otherQuantity) otherQuantity.hidden = !expanded;
  };

  const cup002NativeLidValue = (type, color) => {
    if (type === 'None') return 'No Lid';
    if (!['Press&Close', 'Sip'].includes(type) || !['White', 'Black'].includes(color)) return '';
    return `${type} / ${color}`;
  };

  const parseCup002NativeLid = (value, noLidColor = 'White') => {
    if (value === 'No Lid') return { type: 'None', color: '' };
    const [type, color] = String(value || '').split(' / ');
    return cup002NativeLidValue(type, color) === value && value
      ? { type, color } : { type: '', color: '' };
  };

  const bag004NativeComboValue = (printSides, printColors, valve) => {
    if (!['1 Side', '2 Sides'].includes(printSides)
      || !['1 Color', 'Multicolor'].includes(printColors)
      || !['No Valve', 'With Valve'].includes(valve)) return '';
    return `${printSides} / ${printColors} / ${valve}`;
  };

  const parseBag004NativeCombo = (value) => {
    const [printSides, printColors, valve] = String(value || '').split(' / ');
    return bag004NativeComboValue(printSides, printColors, valve) === value
      ? { printSides, printColors, valve }
      : { printSides: '', printColors: '', valve: '' };
  };

  const initAnchoredSelect = (select) => {
    const wrapper = select?.closest('[data-11p-select]');
    if (!select || !wrapper || select.dataset.elevenpSelectReady === 'true') return;
    select.dataset.elevenpSelectReady = 'true';
    wrapper.classList.add('is-enhanced');
    const searchable = wrapper.dataset.elevenpSelectSearchable === 'true';
    const appSelect = wrapper.dataset.elevenpAppSelect === 'true';

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'elevenp-select-trigger';
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');

    const menu = document.createElement('div');
    menu.className = 'elevenp-select-menu';
    menu.hidden = true;

    const optionList = document.createElement('div');
    optionList.className = 'elevenp-select-options';
    optionList.setAttribute('role', 'listbox');

    let searchInput = null;
    let emptyState = null;
    if (searchable) {
      const searchWrap = document.createElement('div');
      searchWrap.className = 'elevenp-select-search';
      searchInput = document.createElement('input');
      searchInput.type = 'search';
      searchInput.autocomplete = 'off';
      searchInput.spellcheck = false;
      searchInput.placeholder = 'Search country or calling code';
      searchInput.setAttribute('aria-label', 'Search country or international calling code');
      searchWrap.append(searchInput);
      menu.append(searchWrap);

      emptyState = document.createElement('p');
      emptyState.className = 'elevenp-select-empty';
      emptyState.textContent = 'No matching country';
      emptyState.hidden = true;
    }
    menu.append(optionList);
    if (emptyState) menu.append(emptyState);

    const items = [];

    const makeChoice = (option, target) => {
      target.replaceChildren();
      const imageUrl = option?.dataset.imageUrl;
      const requestedImageSize = ['small', 'medium', 'large'].includes(option?.dataset.imageSize)
        ? option.dataset.imageSize
        : 'small';
      const imageSize = target === trigger ? 'small' : requestedImageSize;
      if (imageUrl) {
        const imageWrap = document.createElement('span');
        imageWrap.className = `elevenp-select-visual is-${imageSize}`;
        imageWrap.setAttribute('aria-hidden', 'true');
        const image = document.createElement('img');
        image.src = imageUrl;
        image.alt = '';
        image.loading = target === trigger ? 'eager' : 'lazy';
        imageWrap.append(image);
        target.append(imageWrap);
      }
      const dialCode = option?.dataset.dialCode || '';
      if (dialCode) {
        const code = document.createElement('span');
        code.className = 'elevenp-select-code';
        code.textContent = `+${dialCode}`;
        target.append(code);
      }
      const label = document.createElement('span');
      label.className = 'elevenp-select-choice-label';
      label.textContent = option?.dataset.valueLabel || option?.textContent?.trim() || '';
      target.append(label);
    };

    const filterOptions = (query = '') => {
      const rawQuery = String(query).trim();
      const normalizedQuery = rawQuery.toLocaleLowerCase('en-US');
      const dialQuery = rawQuery.replace(/[^0-9]/g, '');
      const isDialQuery = /^\+?[0-9]+$/.test(rawQuery);
      const exactDialCodeExists = isDialQuery && items.some((_, index) => (
        (select.options[index]?.dataset.dialCode || '') === dialQuery
      ));
      let visibleCount = 0;
      items.forEach((item, index) => {
        const option = select.options[index];
        const searchValue = option?.dataset.searchValue
          || `${option?.textContent || ''} ${option?.dataset.countryCode || ''}`;
        const dialCode = option?.dataset.dialCode || '';
        const visible = !normalizedQuery
          || (exactDialCodeExists
            ? dialCode === dialQuery
            : searchValue.toLocaleLowerCase('en-US').includes(normalizedQuery));
        item.hidden = !visible;
        if (visible) visibleCount += 1;
      });
      if (emptyState) emptyState.hidden = visibleCount > 0;
    };

    const visibleItems = () => items.filter((item) => !item.hidden);

    const close = () => {
      wrapper.classList.remove('is-open', 'opens-upward');
      trigger.setAttribute('aria-expanded', 'false');
      menu.hidden = true;
      if (searchInput) {
        searchInput.value = '';
        filterOptions();
      }
    };

    const open = () => {
      if (select.disabled) return;
      if (appSelect) update();
      document.querySelectorAll('[data-11p-select].is-open').forEach((candidate) => {
        if (candidate !== wrapper) candidate.querySelector('.elevenp-select-trigger')?.click();
      });
      menu.hidden = false;
      wrapper.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      window.requestAnimationFrame(() => {
        const triggerRect = trigger.getBoundingClientRect();
        const menuHeight = Math.min(menu.scrollHeight, window.innerHeight * .48);
        const spaceBelow = window.innerHeight - triggerRect.bottom;
        const forceDown = wrapper.dataset.elevenpSelectDirection === 'down'
          || Boolean(wrapper.closest('[data-11p-tp], [data-11p-rq], .elevenp-sp-card'));
        wrapper.classList.toggle('opens-upward', !forceDown && spaceBelow < menuHeight + 12 && triggerRect.top > menuHeight + 12);
        const selectedItem = optionList.querySelector('[aria-selected="true"]');
        if (selectedItem) {
          const itemTop = selectedItem.offsetTop;
          const itemBottom = itemTop + selectedItem.offsetHeight;
          if (itemTop < optionList.scrollTop) optionList.scrollTop = itemTop;
          else if (itemBottom > optionList.scrollTop + optionList.clientHeight) {
            optionList.scrollTop = itemBottom - optionList.clientHeight;
          }
        }
        searchInput?.focus({ preventScroll: true });
      });
    };

    const update = () => {
      const option = select.options[select.selectedIndex];
      makeChoice(option, trigger);
      trigger.disabled = select.disabled;
      trigger.setAttribute('aria-label', `${select.getAttribute('aria-label') || select.closest('label')?.querySelector(':scope > span:first-child')?.textContent?.trim() || 'Select'}: ${option?.textContent?.trim() || ''}`);
      items.forEach((item, index) => {
        item.setAttribute('aria-selected', String(index === select.selectedIndex));
        item.classList.toggle('is-selected', index === select.selectedIndex);
        item.hidden = Boolean(select.options[index]?.hidden || select.options[index]?.disabled
          || (appSelect && select.options[index]?.classList.contains('ymq-option-select-placeholder')));
      });
    };

    const buildItems = () => {
      items.length = 0;
      optionList.replaceChildren();
      [...select.options].forEach((option, index) => {
        const item = document.createElement('button');
        item.type = 'button';
        item.className = 'elevenp-select-option';
        item.dataset.dialCode = option?.dataset.dialCode || '';
        item.setAttribute('role', 'option');
        item.dataset.optionIndex = String(index);
        makeChoice(option, item);
        item.addEventListener('click', () => {
          if (select.disabled || option.disabled || option.hidden) return;
          if (select.selectedIndex !== index) {
            select.selectedIndex = index;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
          close();
          trigger.focus();
        });
        items.push(item);
        optionList.append(item);
      });
    };
    buildItems();

    trigger.addEventListener('click', () => {
      if (trigger.getAttribute('aria-expanded') === 'true') close();
      else open();
    });
    trigger.addEventListener('keydown', (event) => {
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End', 'Escape'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'Escape') {
        close();
        return;
      }
      if (trigger.getAttribute('aria-expanded') !== 'true') open();
      const current = select.selectedIndex;
      const next = event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? select.options.length - 1
          : Math.max(0, Math.min(select.options.length - 1, current + (event.key === 'ArrowUp' ? -1 : 1)));
      if (appSelect) {
        const available = visibleItems();
        const target = event.key === 'Home' ? available[0]
          : event.key === 'End' ? available.at(-1)
            : items[next]?.hidden ? (event.key === 'ArrowUp' ? available.at(-1) : available[0]) : items[next];
        target?.focus();
      } else {
        items[next]?.focus();
      }
    });
    menu.addEventListener('keydown', (event) => {
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End', 'Escape'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'Escape') {
        close();
        trigger.focus();
        return;
      }
      const availableItems = visibleItems();
      if (!availableItems.length) return;
      if (event.target === searchInput) {
        const searchDestination = event.key === 'ArrowUp' || event.key === 'End'
          ? availableItems.at(-1)
          : availableItems[0];
        searchDestination?.focus();
        return;
      }
      const current = Math.max(0, availableItems.indexOf(document.activeElement));
      const next = event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? availableItems.length - 1
          : Math.max(0, Math.min(availableItems.length - 1, current + (event.key === 'ArrowUp' ? -1 : 1)));
      availableItems[next]?.focus();
    });
    searchInput?.addEventListener('input', () => filterOptions(searchInput.value));
    select.addEventListener('change', update);
    document.addEventListener('pointerdown', (event) => {
      if (!wrapper.contains(event.target)) close();
    });
    window.addEventListener('resize', close);
    window.addEventListener('scroll', (event) => {
      if (!wrapper.contains(event.target)) close();
    }, true);

    select.setAttribute('aria-hidden', 'true');
    select.tabIndex = -1;
    wrapper.append(trigger, menu);
    select._elevenpRefreshSelect = update;
    select._elevenpRebuildSelect = () => {
      buildItems();
      update();
    };
    if (appSelect) {
      const field = select.closest('.ymq-options-box');
      trigger.setAttribute('aria-required', String(field?.getAttribute('ymq-option-validate')?.includes('required') || select.required));
      const error = field?.querySelector('.ymq-option-error');
      if (error) {
        if (!error.id) error.id = `${select.id}-error`;
        trigger.setAttribute('aria-describedby', error.id);
      }
      select.addEventListener('focus', () => trigger.focus());
      select.addEventListener('invalid', (event) => {
        event.preventDefault();
        trigger.focus();
      });
      const optionsObserver = new MutationObserver(() => {
        buildItems();
        update();
      });
      optionsObserver.observe(select, {
        childList: true, subtree: true, characterData: true, attributes: true,
        attributeFilter: ['disabled', 'hidden', 'selected', 'label', 'value']
      });
    }
    update();
  };

  const initRfqForm = (scope = document) => {
    scope.querySelectorAll('.elevenp-rq-form [data-11p-select] select').forEach(initAnchoredSelect);

    scope.querySelectorAll('[data-11p-rq-shaped-input]:not([data-11p-shape-ready])').forEach((group) => {
      const select = group.querySelector('[data-11p-rq-shape-select]');
      const panels = [...group.querySelectorAll('[data-11p-rq-shape-panel]')];
      if (!select || !panels.length) return;
      group.setAttribute('data-11p-shape-ready', 'true');

      const sync = () => {
        panels.forEach((panel) => {
          const active = Boolean(select.value)
            && panel.getAttribute('data-11p-rq-shape-panel') === select.value;
          panel.hidden = !active;
          panel.querySelectorAll('[data-11p-rq-shape-dimension]').forEach((input) => {
            if (!active) input.value = '';
            input.disabled = !active;
            input.required = active && input.getAttribute('data-11p-rq-required') === 'true';
          });
        });
      };

      select.addEventListener('change', sync);
      sync();
    });

    scope.querySelectorAll('[data-11p-rq]:not([data-11p-option-matrix-ready])').forEach((root) => {
      root.setAttribute('data-11p-option-matrix-ready', 'true');
      const source = root.querySelector('[data-11p-rq-option-matrix]');
      const selects = [...root.querySelectorAll('[data-11p-rq-option]')];
      if (!source || selects.length < 2) return;

      let matrix;
      try {
        matrix = JSON.parse(source.textContent || '{}');
      } catch (_) {
        return;
      }
      if (matrix?.complete !== true || !Array.isArray(matrix.rows)) return;

      const normalize = (value) => String(value ?? '').trim().toLocaleLowerCase('en-US');
      const notApplicableValues = new Set(['—', '-', 'n/a', 'not applicable', 'not_applicable']);
      const supportedStatuses = new Set(['', 'active', 'quote only', 'quote_only']);
      const rows = matrix.rows.filter((row) => (
        row && typeof row === 'object' && supportedStatuses.has(normalize(row.status))
      ));
      if (!rows.length) return;

      const fields = selects.map((select) => ({
        select,
        field: select.closest('[data-11p-rq-option-field]'),
        key: select.dataset.optionKey,
      })).filter((field) => field.key);
      const status = root.querySelector('[data-11p-rq-option-status]');
      const rowValue = (row, key) => normalize(row[key]);

      const refresh = () => {
        let clearedLabel = '';

        fields.forEach(({ select, field, key }, fieldIndex) => {
          const upstreamFields = fields.slice(0, fieldIndex);
          const candidates = rows.filter((row) => upstreamFields.every(({ select: upstreamSelect, key: upstreamKey }) => (
            !upstreamSelect.value || rowValue(row, upstreamKey) === normalize(upstreamSelect.value)
          )));
          const applicableValues = new Set(candidates.map((row) => rowValue(row, key)));
          const notApplicable = candidates.length > 0
            && [...applicableValues].every((value) => notApplicableValues.has(value));
          const placeholder = [...select.options].find((option) => !option.value);
          if (placeholder && !placeholder.dataset.elevenpOriginalLabel) {
            placeholder.dataset.elevenpOriginalLabel = placeholder.textContent.trim();
          }
          if (placeholder) {
            placeholder.textContent = notApplicable
              ? 'Not applicable'
              : placeholder.dataset.elevenpOriginalLabel;
          }

          [...select.options].forEach((option) => {
            if (!option.value) return;
            const unavailable = notApplicable || !applicableValues.has(normalize(option.value));
            option.disabled = unavailable;
            option.hidden = unavailable;
          });

          if (notApplicable) {
            if (select.value) clearedLabel = field?.querySelector(':scope > span:first-child')?.textContent?.trim() || '';
            select.value = '';
            select.disabled = true;
          } else {
            select.disabled = false;
            const selectedOption = select.options[select.selectedIndex];
            if (select.value && selectedOption?.disabled) {
              clearedLabel = field?.querySelector(':scope > span:first-child')?.textContent?.trim() || '';
              select.value = '';
            }
          }

          field?.classList.toggle('is-not-applicable', notApplicable);
          field?.toggleAttribute('data-11p-not-applicable', notApplicable);
          select._elevenpRefreshSelect?.();
        });

        if (status) {
          status.textContent = clearedLabel
            ? `${clearedLabel.replace(/\s*\*\s*$/, '')} was cleared because it is not available with the selected options.`
            : '';
        }
      };

      fields.forEach(({ select }) => select.addEventListener('change', refresh));
      refresh();
    });

    scope.querySelectorAll('[data-11p-quantity-select]:not([data-11p-ready])').forEach((select) => {
      select.setAttribute('data-11p-ready', 'true');
      const form = select.closest('form');
      const otherField = form?.querySelector('[data-11p-other-quantity]');
      const otherInput = otherField?.querySelector('input');

      const sync = () => {
        const showOther = select.value === 'other';
        if (otherField) otherField.hidden = !showOther;
        if (otherInput) otherInput.required = showOther;
      };

      select.addEventListener('change', sync);
      sync();
    });

    scope.querySelectorAll('[data-11p-form-success]:not([data-11p-focused])').forEach((success) => {
      success.setAttribute('data-11p-focused', 'true');
      success.focus({ preventScroll: true });
    });
  };

  const initFaqGroups = (scope = document) => {
    scope.querySelectorAll('[data-11p-faq-group]:not([data-11p-ready])').forEach((group) => {
      group.setAttribute('data-11p-ready', 'true');
      const items = [...group.querySelectorAll('[data-11p-faq-item]')];
      items.forEach((item) => {
        item.addEventListener('toggle', () => {
          if (!item.open) return;
          items.forEach((other) => {
            if (other !== item) other.open = false;
          });
        });
      });
    });
  };

  const initProductZoom = (scope = document) => {
    scope.querySelectorAll('[data-11p-image-zoom="lightbox"] .elevenp-product-media:not([data-11p-zoom-ready])').forEach((media) => {
      media.setAttribute('data-11p-zoom-ready', 'true');
      const lens = document.createElement('span');
      const preview = document.createElement('span');
      const stickyColumn = media.closest('[data-11p-smart-sticky]');
      lens.className = 'elevenp-gallery-lens';
      preview.className = 'elevenp-gallery-zoom';
      lens.setAttribute('aria-hidden', 'true');
      preview.setAttribute('aria-hidden', 'true');
      media.append(lens, preview);

      const clear = () => {
        media.classList.remove('is-zooming');
        stickyColumn?.classList.remove('is-zoom-layer-active');
        lens.removeAttribute('style');
        preview.removeAttribute('style');
      };

      const navigationButtons = media.querySelectorAll(
        'media-gallery > slider-component:first-of-type > .slider-buttons .slider-button',
      );
      navigationButtons.forEach((button) => {
        button.addEventListener('pointerenter', clear);
        button.addEventListener('pointerdown', clear);
        button.addEventListener('focusin', clear);
      });

      media.addEventListener('pointermove', (event) => {
        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return clear();
        if (event.target instanceof Element && event.target.closest('.slider-button')) return clear();
        const activeItem = media.querySelector('.product__media-item.is-active');
        const container = activeItem?.querySelector('.product-media-container');
        if (!container) return clear();
        const image = container.querySelector('img');
        if (!image) return clear();

        const rect = container.getBoundingClientRect();
        const imageStyle = window.getComputedStyle(image);
        const sourceWidth = image.naturalWidth || rect.width;
        const sourceHeight = image.naturalHeight || rect.height;
        const fitScale = imageStyle.objectFit === 'contain'
          ? Math.min(rect.width / sourceWidth, rect.height / sourceHeight)
          : Math.max(rect.width / sourceWidth, rect.height / sourceHeight);
        const renderedWidth = sourceWidth * fitScale;
        const renderedHeight = sourceHeight * fitScale;
        const objectLeft = (rect.width - renderedWidth) / 2;
        const objectTop = (rect.height - renderedHeight) / 2;
        const isInsideRenderedImage = event.clientX >= rect.left + objectLeft
          && event.clientX <= rect.left + objectLeft + renderedWidth
          && event.clientY >= rect.top + objectTop
          && event.clientY <= rect.top + objectTop + renderedHeight;
        if (!isInsideRenderedImage) return clear();
        const magnification = 2;
        const previewSize = preview.getBoundingClientRect().width || 360;
        const lensSize = Math.min(previewSize / magnification, renderedWidth, renderedHeight);
        const rawX = event.clientX - rect.left;
        const rawY = event.clientY - rect.top;
        const x = Math.max(
          objectLeft + lensSize / 2,
          Math.min(rawX, objectLeft + renderedWidth - lensSize / 2),
        );
        const y = Math.max(
          objectTop + lensSize / 2,
          Math.min(rawY, objectTop + renderedHeight - lensSize / 2),
        );
        const lensLeft = x - lensSize / 2;
        const lensTop = y - lensSize / 2;
        const viewportGutter = 16;
        const pointerGap = 24;
        const previewTop = Math.max(
          96,
          Math.min(rect.top, window.innerHeight - previewSize - viewportGutter),
        );
        let previewLeft = event.clientX + pointerGap;
        if (previewLeft + previewSize > window.innerWidth - viewportGutter) {
          previewLeft = event.clientX - previewSize - pointerGap;
        }
        previewLeft = Math.max(
          viewportGutter,
          Math.min(previewLeft, window.innerWidth - previewSize - viewportGutter),
        );
        const responsiveImageUrl = image.currentSrc || image.src;
        let zoomImageUrl = responsiveImageUrl;
        try {
          const parsedZoomUrl = new URL(responsiveImageUrl, window.location.href);
          if (parsedZoomUrl.hostname === 'cdn.shopify.com') {
            parsedZoomUrl.searchParams.set('width', String(Math.ceil(renderedWidth * magnification)));
            zoomImageUrl = parsedZoomUrl.toString();
          }
        } catch (_) {
          zoomImageUrl = responsiveImageUrl;
        }

        lens.style.width = `${lensSize}px`;
        lens.style.height = `${lensSize}px`;
        lens.style.left = `${x}px`;
        lens.style.top = `${y}px`;
        preview.style.backgroundImage = `url("${zoomImageUrl}")`;
        preview.style.backgroundSize = `${renderedWidth * magnification}px ${renderedHeight * magnification}px`;
        preview.style.backgroundPosition = `${-(lensLeft - objectLeft) * magnification}px ${-(lensTop - objectTop) * magnification}px`;
        preview.style.top = `${previewTop}px`;
        preview.style.left = `${previewLeft}px`;
        stickyColumn?.classList.add('is-zoom-layer-active');
        media.classList.add('is-zooming');
      });

      media.addEventListener('pointerleave', clear);
      media.addEventListener('click', clear);
    });
  };

  const initGalleryNavigation = (scope = document) => {
    scope.querySelectorAll('.elevenp-product-media:not([data-11p-navigation-ready])').forEach((media) => {
      const viewer = media.querySelector('media-gallery > slider-component:first-of-type');
      const previous = viewer?.querySelector(':scope > .slider-buttons .slider-button--prev');
      const next = viewer?.querySelector(':scope > .slider-buttons .slider-button--next');
      const thumbnails = [...media.querySelectorAll('.thumbnail-list .thumbnail')];
      if (!previous || !next || thumbnails.length < 2) return;

      media.setAttribute('data-11p-navigation-ready', 'true');
      const move = (direction) => {
        const currentIndex = Math.max(0, thumbnails.findIndex((thumbnail) => thumbnail.getAttribute('aria-current') === 'true'));
        const targetIndex = (currentIndex + direction + thumbnails.length) % thumbnails.length;
        thumbnails[targetIndex]?.click();
      };
      previous.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        move(-1);
      }, true);
      next.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        move(1);
      }, true);
    });
  };

  const initSmartSticky = (scope = document) => {
    scope.querySelectorAll('.elevenp-product-layout:not([data-11p-smart-sticky-ready])').forEach((layout) => {
      const stickyItems = [...layout.querySelectorAll(':scope > [data-11p-smart-sticky]')];
      if (!stickyItems.length) return;

      layout.setAttribute('data-11p-smart-sticky-ready', 'true');
      const desktopLayout = window.matchMedia('(min-width: 900px)');
      const header = document.querySelector('.elevenp-header-section');
      let updateFrame = 0;
      let resizeObserver;

      const updateStickyOffsets = () => {
        window.cancelAnimationFrame(updateFrame);
        updateFrame = window.requestAnimationFrame(() => {
          if (!layout.isConnected) {
            resizeObserver?.disconnect();
            window.removeEventListener('resize', updateStickyOffsets);
            desktopLayout.removeEventListener?.('change', updateStickyOffsets);
            return;
          }

          if (!desktopLayout.matches) {
            stickyItems.forEach((item) => {
              item.style.removeProperty('--11p-smart-sticky-top');
              item.removeAttribute('data-11p-sticky-mode');
            });
            return;
          }

          const headerHeight = header?.getBoundingClientRect().height || 72;
          const topGap = 20;
          const bottomGap = 24;
          const preferredTop = headerHeight + topGap;

          stickyItems.forEach((item) => {
            const itemHeight = item.getBoundingClientRect().height;
            const availableHeight = window.innerHeight - preferredTop - bottomGap;
            const stickyTop = itemHeight <= availableHeight
              ? preferredTop
              : window.innerHeight - bottomGap - itemHeight;

            item.style.setProperty('--11p-smart-sticky-top', `${Math.round(stickyTop)}px`);
            item.setAttribute('data-11p-sticky-mode', itemHeight <= availableHeight ? 'top' : 'bottom');
          });
        });
      };

      window.addEventListener('resize', updateStickyOffsets, { passive: true });
      desktopLayout.addEventListener?.('change', updateStickyOffsets);
      document.fonts?.ready.then(updateStickyOffsets);

      if ('ResizeObserver' in window) {
        resizeObserver = new ResizeObserver(updateStickyOffsets);
        stickyItems.forEach((item) => resizeObserver.observe(item));
      }

      updateStickyOffsets();
    });
  };

  const initTp = (scope = document) => {
    scope.querySelectorAll('[data-11p-tp]:not([data-11p-ready])').forEach((root) => {
      root.setAttribute('data-11p-ready', 'true');
      root.setAttribute('data-11p-core-form-ready', 'true');
      const configuredMatrix = root.dataset.nativeMatrix === 'configured';
      let configuredMatrixConfig = {};
      try {
        configuredMatrixConfig = JSON.parse(root.querySelector('[data-11p-tp-configured-matrix]')?.textContent || '{}');
      } catch (_) {
        configuredMatrixConfig = {};
      }
      let quantityTierMatrix = {};
      try {
        quantityTierMatrix = JSON.parse(root.querySelector('[data-11p-quantity-tier-matrix]')?.textContent || '{}');
      } catch (_) {
        quantityTierMatrix = {};
      }
      const sparseQuantityTiers = quantityTierMatrix.enabled === true;
      const configuredMatrixSeparator = typeof configuredMatrixConfig.separator === 'string'
        ? configuredMatrixConfig.separator
        : ' / ';
      const configuredMatrixAttributes = Array.isArray(configuredMatrixConfig.attributes)
        ? configuredMatrixConfig.attributes.filter((attribute) => typeof attribute?.label === 'string' && attribute.label.trim())
        : [];
      const configuredMatrixOption = root.querySelector('[data-11p-configured-matrix-option]');
      const configuredMatrixControls = root.querySelector('[data-11p-configured-matrix-controls]');
      const configuredYmqBindings = [];
      const mappedPresentation = Object.prototype.hasOwnProperty.call(configuredMatrixConfig, 'presentation_combinations');
      const configuredMatrixRows = configuredPresentationRows(
        configuredMatrixConfig,
        [...(configuredMatrixOption?.options || [])].filter((option) => option.dataset.optionValueId).map((option) => option.value),
        configuredMatrixAttributes.length,
        configuredMatrixSeparator,
      );
      const configuredNativeValue = (values) => configuredMatrixRows.find((row) => (
        row.values.length === values.length && row.values.every((value, index) => value === values[index])
      ))?.native || '';
      if (configuredMatrix && configuredMatrixOption && configuredMatrixControls && configuredMatrixAttributes.length) {
        const selectedParts = configuredMatrixRows.find((row) => row.native === configuredMatrixOption.value)?.values || [];
        const validCombinations = configuredMatrixRows.map((row) => row.values);
        if (validCombinations.length) {
          configuredMatrixAttributes.forEach((attribute, index) => {
            const configuredImages = attribute.images && typeof attribute.images === 'object'
              ? attribute.images
              : {};
            const configuredImageSize = ['small', 'medium', 'large'].includes(attribute.image_size)
              ? attribute.image_size
              : 'large';
            const label = document.createElement('label');
            label.className = 'elevenp-field';
            const labelText = document.createElement('span');
            labelText.textContent = attribute.label.trim();
            const selectWrap = document.createElement('span');
            selectWrap.className = 'elevenp-select';
            selectWrap.setAttribute('data-11p-select', '');
            const select = document.createElement('select');
            select.name = `properties[${attribute.label.trim()}]`;
            select.setAttribute('data-11p-configured-matrix-part', '');
            select.dataset.configuredMatrixIndex = String(index);
            [...new Set(validCombinations.map((parts) => parts[index]))].forEach((value) => {
              const option = document.createElement('option');
              option.value = value;
              option.textContent = value;
              option.selected = value === selectedParts[index];
              if (typeof configuredImages[value] === 'string' && configuredImages[value].trim()) {
                option.dataset.imageUrl = configuredImages[value].trim();
                option.dataset.imageSize = configuredImageSize;
              }
              select.append(option);
            });
            const caret = root.querySelector('.elevenp-select .svg-wrapper')?.cloneNode(true) || document.createElement('span');
            caret.className = 'svg-wrapper';
            caret.setAttribute('aria-hidden', 'true');
            selectWrap.append(select, caret);
            label.append(labelText, selectWrap);
            configuredMatrixControls.append(label);
            const selectedConfiguredImageUrl = select.options[select.selectedIndex]?.dataset.imageUrl;
            if (selectedConfiguredImageUrl) {
              label.classList.add('elevenp-image-select-pending');
              const preload = new Image();
              const reveal = () => label.classList.remove('elevenp-image-select-pending');
              preload.addEventListener('load', reveal, { once: true });
              preload.addEventListener('error', reveal, { once: true });
              preload.src = selectedConfiguredImageUrl;
              if (preload.complete) reveal();
            }
            if (attribute.source === 'ymq') {
              const hasConfiguredImages = [...select.options].every((option) => option.dataset.imageUrl);
              if (!hasConfiguredImages) label.classList.add('elevenp-image-select-pending');
              configuredYmqBindings.push({ attribute, label, select, sourceField: null });
            }
          });
        }
      }

      const normalizeConfiguredLabel = (value) => String(value || '').replace(/^_+/, '').trim().toLowerCase();
      const bindConfiguredYmqOptions = () => {
        let allBound = true;
        configuredYmqBindings.forEach((binding) => {
          if (binding.label.dataset.ymqReady === 'true') return;
          const expectedLabel = normalizeConfiguredLabel(binding.attribute.ymq_label || binding.attribute.label);
          const field = [...document.querySelectorAll('[ymq-option-label]')].find((candidate) => (
            normalizeConfiguredLabel(candidate.getAttribute('ymq-option-label')) === expectedLabel
          ));
          if (!field) {
            allBound = false;
            return;
          }
          binding.label.dataset.ymqReady = 'true';
          binding.sourceField = field;
          const radios = [...field.querySelectorAll('input[type="radio"]')];
          const syncSourceFromYmq = (radio) => {
            if (!radio?.checked || ![...binding.select.options].some((option) => option.value === radio.value)) return;
            if (binding.select.value !== radio.value) {
              binding.select.value = radio.value;
              binding.select.dispatchEvent(new Event('change', { bubbles: true }));
            }
          };
          radios.forEach((radio) => {
            radio.setAttribute('aria-label', radio.value);
            const image = radio.closest('label')?.querySelector('img');
            if (image) image.alt = radio.value;
            const option = [...binding.select.options].find((candidate) => candidate.value === radio.value);
            const imageUrl = image?.currentSrc || image?.src || '';
            if (option && imageUrl) {
              option.dataset.imageUrl = imageUrl;
              option.dataset.imageSize = ['small', 'medium', 'large'].includes(binding.attribute.image_size)
                ? binding.attribute.image_size
                : 'large';
            }
            radio.addEventListener('change', () => syncSourceFromYmq(radio));
          });
          const selectedRadio = radios.find((radio) => radio.value === binding.select.value);
          if (selectedRadio && !selectedRadio.checked) selectedRadio.click();
          binding.select.addEventListener('change', () => {
            const matchingRadio = radios.find((radio) => radio.value === binding.select.value);
            if (matchingRadio && !matchingRadio.checked) matchingRadio.click();
          });
          if (binding.select._elevenpRebuildSelect) binding.select._elevenpRebuildSelect();
          else binding.select._elevenpRefreshSelect?.();
          const selectedImageUrl = binding.select.options[binding.select.selectedIndex]?.dataset.imageUrl;
          if (selectedImageUrl && binding.label.classList.contains('elevenp-image-select-pending')) {
            const preload = new Image();
            const reveal = () => binding.label.classList.remove('elevenp-image-select-pending');
            preload.addEventListener('load', reveal, { once: true });
            preload.addEventListener('error', reveal, { once: true });
            preload.src = selectedImageUrl;
            if (preload.complete) reveal();
          } else if (!selectedImageUrl) {
            binding.label.classList.remove('elevenp-image-select-pending');
          }
        });
        return allBound;
      };
      if (configuredYmqBindings.length && !bindConfiguredYmqOptions()) {
        const ymqObserver = new MutationObserver(() => {
          if (bindConfiguredYmqOptions()) ymqObserver.disconnect();
        });
        ymqObserver.observe(document.documentElement, { childList: true, subtree: true });
        window.setTimeout(() => {
          configuredYmqBindings.forEach((binding) => {
            if (binding.label.dataset.ymqReady === 'true') return;
            binding.label.dataset.ymqFallback = 'true';
            binding.label.classList.remove('elevenp-image-select-pending');
          });
        }, 3500);
      }
      root.setAttribute('data-11p-options-ready', 'true');
      root.querySelector('[data-11p-tp-options]')?.setAttribute('aria-busy', 'false');
      const cup002NativeMatrix = ['cup002', 'cup011'].includes(root.dataset.nativeMatrix);
            const bag001NativeMatrix = ['bag001', 'bag006', 'bag008', 'bag009', 'bag010'].includes(root.dataset.nativeMatrix);
      const bagCompoundSeparator = ['bag006', 'bag008', 'bag009', 'bag010'].includes(root.dataset.nativeMatrix) ? ' / ' : ' · ';
      const bag004NativeMatrix = root.dataset.nativeMatrix === 'bag004';
      const cup018NativeMatrix = root.dataset.nativeMatrix === 'cup018';
      const nativeMatrix = configuredMatrix
        || root.dataset.nativeMatrix === 'cup001'
        || root.dataset.nativeMatrix === 'cup004'
        || cup018NativeMatrix
        || cup002NativeMatrix
        || bag001NativeMatrix
        || bag004NativeMatrix;
      const variantsNode = root.querySelector('[data-11p-tp-variants]');
      let variants = [];
      try {
        variants = JSON.parse(variantsNode?.textContent || '[]');
      } catch (_) {
        variants = [];
      }

      const quantityIndex = Number(root.dataset.quantityOptionIndex);
      const currency = root.dataset.currency || 'USD';
      const locale = root.dataset.locale || 'en-US';
      const optionSelects = [...root.querySelectorAll('[data-11p-tp-option]')];
      const priceOptionSelects = [...root.querySelectorAll('[data-11p-tp-price-option]')];
      const rows = [...root.querySelectorAll('[data-11p-tier-row]')];
      if (nativeMatrix) prepareCup001QuantityRows(root, rows, locale);
      const variantInput = root.querySelector('[data-11p-tp-variant-id]');
      const productionQuantity = root.querySelector('[data-11p-tp-production-quantity]');
      const total = root.querySelector('[data-11p-tp-total]');
      const totalQuantity = root.querySelector('[data-11p-tp-total-quantity]');
      const stepQuantitySummaries = [...root.querySelectorAll('[data-11p-step-quantity]')];
      const stepTotalSummaries = [...root.querySelectorAll('[data-11p-step-total]')];
      const submitButton = root.querySelector('[type="submit"][name="add"]');
      const productForm = root.querySelector('form.elevenp-tp-form');
      const artworkHost = root.querySelector('[data-11p-artwork-host]');
      const artworkProperty = root.querySelector('[data-11p-tp-artwork-property]');
      const artworkEmailWrap = root.querySelector('[data-11p-artwork-email-wrap]');
      const artworkEmail = root.querySelector('[data-11p-artwork-email]');
      const artworkEmailLabel = artworkEmail?.querySelector('span');
      const artworkCopyStatus = root.querySelector('[data-11p-artwork-copy-status]');
      const artworkError = root.querySelector('[data-11p-artwork-error]');
      const artworkMethod = root.querySelector('[data-11p-artwork-method]');
      const artworkUpload = root.querySelector('[data-11p-artwork-upload]');
      const artworkDropzone = root.querySelector('.elevenp-artwork-dropzone');
      const artworkFile = root.querySelector('[data-11p-artwork-file]');
      const artworkFileName = root.querySelector('[data-11p-artwork-file-name]');
      const artworkStatus = root.querySelector('[data-11p-artwork-status]');
      const priceUnavailable = root.querySelector('[data-11p-price-unavailable]');
      const priceUnavailableMessage = priceUnavailable?.textContent || '';
      let selectedVariant = null;
      let ymqArtwork = null;
      let ymqMethodSelect = null;
      let ymqUploadInput = null;
      let ymqArtworkObserver = null;
      let observedYmqArtwork = null;
      let artworkReference = '';
      let artworkSelectedName = '';
      let artworkUploadFailureReason = '';
      let artworkUploadStartedAt = 0;
      let artworkFailureCandidate = '';
      let artworkFailureCandidateSince = 0;
      let artworkUploadMonitor = null;
      let artworkCopyFeedbackTimer = null;
      let lastBridgedArtworkFileSignature = '';
      let lastBridgedYmqUploadInput = null;
      let ymqAdditionalInput = null;
      let ymqAdditionalTotal = null;
      let lastSyncedAddOnCents = null;
      let nativePricingReady = !nativeMatrix;
      let nativePricingLoading = false;
      let nativeRequestSequence = 0;
      let nativePriceRequest = null;
      let restoringConfiguration = false;
      const configurationStorageKey = `elevenp_tp_configuration_v1_${root.dataset.productId || 'product'}`;
      const compoundSelect = root.querySelector('[data-11p-compound-option]');
      const compoundPrint = root.querySelector('[data-11p-compound-print]');
      const compoundLid = root.querySelector('[data-11p-compound-lid]');
      const bag001CompoundParts = [...root.querySelectorAll('[data-11p-bag001-compound-part]')];
      const cup002NativeLid = root.querySelector('[data-11p-cup002-native-lid]');
      const cup002LidType = root.querySelector('[data-11p-cup002-lid-type]');
      const cup002LidColor = root.querySelector('[data-11p-cup002-lid-color]');
      const bag004NativeCombo = root.querySelector('[data-11p-bag004-native-combo]');
      const bag004PrintSides = root.querySelector('[data-11p-bag004-print-sides]');
      const bag004PrintColors = root.querySelector('[data-11p-bag004-print-colors]');
      const bag004Valve = root.querySelector('[data-11p-bag004-valve]');
      const configuredMatrixParts = [...root.querySelectorAll('[data-11p-configured-matrix-part]')];
      const cup018SizeSelect = root.querySelector('[data-11p-cup018-size]');
      const cup018SizeQuantitySelect = root.querySelector('[data-11p-cup018-size-quantity]');
      if (cup018NativeMatrix && cup018SizeSelect && cup018SizeQuantitySelect) {
        cup018SizeSelect.value = cup018SizeFromOption(cup018SizeQuantitySelect.value);
      }
      if (cup002NativeMatrix && cup002NativeLid && cup002LidType && cup002LidColor) {
        const initialLid = parseCup002NativeLid(cup002NativeLid.value, cup002LidColor.value);
        cup002LidType.value = initialLid.type;
        cup002LidColor.value = initialLid.color;
        cup002LidColor.disabled = initialLid.type === 'None';
      }
      if (bag004NativeMatrix && bag004NativeCombo && bag004PrintSides && bag004PrintColors && bag004Valve) {
        const initialCombo = parseBag004NativeCombo(bag004NativeCombo.value);
        bag004PrintSides.value = initialCombo.printSides;
        bag004PrintColors.value = initialCombo.printColors;
        bag004Valve.value = initialCombo.valve;
      }
      const cup002LidSelectionIsValid = () => {
        if (!cup002NativeMatrix) return true;
        const value = cup002NativeLidValue(cup002LidType?.value, cup002LidColor?.value);
        return Boolean(value && cup002NativeLid?.value === value
          && [...cup002NativeLid.options].some((option) => option.value === value && option.dataset.optionValueId));
      };
      const bag001ConfigurationIsValid = () => {
        if (!bag001NativeMatrix) return true;
        const value = bag001CompoundParts.map((select) => select?.value || '').join(bagCompoundSeparator);
        return Boolean(value && compoundSelect?.value === value
          && [...compoundSelect.options].some((option) => option.value === value && option.dataset.optionValueId));
      };
      const configuredMatrixSelectionIsValid = () => {
        if (!configuredMatrix) return true;
        const value = configuredNativeValue(configuredMatrixParts.map((select) => select?.value || ''));
        return Boolean(configuredMatrixParts.length === configuredMatrixAttributes.length
          && value && configuredMatrixOption?.value === value
          && [...(configuredMatrixOption?.options || [])].some((option) => option.value === value && option.dataset.optionValueId));
      };
      const refreshConfiguredMatrixOptions = () => {
        if (!configuredMatrix || !configuredMatrixOption || !configuredMatrixParts.length) return;
        const nativeValues = new Set([...configuredMatrixOption.options]
          .filter((option) => option.dataset.optionValueId).map((option) => option.value));
        const availableCombinations = configuredMatrixRows.filter((row) => {
          if (!nativeValues.has(row.native)) return false;
          if (!mappedPresentation || !sparseQuantityTiers) return true;
          const options = selectedOptions();
          options[Number(configuredMatrixOption.dataset.optionIndex)] = row.native;
          return Boolean(allowedQuantityTiers(quantityTierMatrix, options, quantityIndex,
            rows.map((tier) => tier.dataset.tierValue))?.size);
        }).map((row) => row.values);
        configuredMatrixParts.forEach((select, index) => {
          const selectedPrefix = configuredMatrixParts.slice(0, index).map((part) => part.value);
          const allowedValues = new Set(availableCombinations
            .filter((parts) => selectedPrefix.every((value, prefixIndex) => parts[prefixIndex] === value))
            .map((parts) => parts[index]));
          const allowedOptions = [...select.options].filter((option) => allowedValues.has(option.value));
          [...select.options].forEach((option) => { option.hidden = !allowedValues.has(option.value); });
          if (!allowedValues.has(select.value)) select.value = allowedOptions[0]?.value || '';
          const inactiveValues = configuredMatrixAttributes[index]?.not_applicable_values;
          if (Array.isArray(inactiveValues)) {
            const notApplicable = allowedValues.size > 0
              && [...allowedValues].every((value) => inactiveValues.includes(value));
            select.disabled = notApplicable;
            const field = select.closest('.elevenp-field');
            field?.classList.toggle('is-not-applicable', notApplicable);
            field?.toggleAttribute('data-11p-not-applicable', notApplicable);
            const trigger = field?.querySelector('.elevenp-select-trigger');
            if (trigger) trigger.style.cursor = notApplicable ? 'not-allowed' : '';
          }
          select._elevenpRefreshSelect?.();
          const ymqBinding = configuredYmqBindings.find((binding) => (
            Number(binding.select.dataset.configuredMatrixIndex) === index
          ));
          if (ymqBinding?.sourceField) {
            ymqBinding.sourceField.querySelectorAll('input[type="radio"]').forEach((radio) => {
              radio.disabled = !allowedValues.has(radio.value);
            });
          }
        });
      };
      const refreshBag001CompoundOptions = () => {
        if (!bag001NativeMatrix || !compoundSelect || !bag001CompoundParts.length) return;
        const availableCombinations = [...compoundSelect.options]
          .filter((option) => option.dataset.optionValueId)
          .map((option) => option.value.split(bagCompoundSeparator))
          .filter((parts) => parts.length === bag001CompoundParts.length);

        bag001CompoundParts.forEach((select, index) => {
          const selectedPrefix = bag001CompoundParts.slice(0, index).map((part) => part.value);
          const allowedValues = new Set(availableCombinations
            .filter((parts) => selectedPrefix.every((value, prefixIndex) => parts[prefixIndex] === value))
            .map((parts) => parts[index]));
          const allowedOptions = [...select.options].filter((option) => allowedValues.has(option.value));

          [...select.options].forEach((option) => {
            option.hidden = !allowedValues.has(option.value);
          });
          if (!allowedValues.has(select.value)) select.value = allowedOptions[0]?.value || '';
          select._elevenpRefreshSelect?.();
        });
      };
      const bag004SelectionIsValid = () => {
        if (!bag004NativeMatrix) return true;
        const value = bag004NativeComboValue(bag004PrintSides?.value, bag004PrintColors?.value, bag004Valve?.value);
        return Boolean(value && bag004NativeCombo?.value === value
          && [...bag004NativeCombo.options].some((option) => option.value === value && option.dataset.optionValueId));
      };

      root.querySelectorAll('[data-11p-select] select').forEach(initAnchoredSelect);

      const selectedOption = (select) => select?.options?.[select.selectedIndex] || null;
      const stopArtworkUploadMonitor = () => {
        if (!artworkUploadMonitor) return;
        window.clearInterval(artworkUploadMonitor);
        artworkUploadMonitor = null;
      };
      const setArtworkStatus = (state, message = '') => {
        if (artworkStatus) {
          artworkStatus.dataset.state = state || 'idle';
          artworkStatus.textContent = message;
          artworkStatus.hidden = !message;
        }
        if (artworkHost) artworkHost.dataset.artworkState = state || 'idle';
        artworkDropzone?.setAttribute('aria-busy', String(state === 'uploading'));
      };
      const clearArtworkError = () => {
        if (artworkError) {
          artworkError.hidden = true;
          artworkError.textContent = '';
        }
        artworkHost?.classList.remove('has-error');
      };
      const showArtworkError = (message) => {
        const isSameMessage = artworkError
          && !artworkError.hidden
          && artworkError.textContent === message;
        if (artworkError) {
          artworkError.textContent = message;
          artworkError.hidden = false;
          if (!isSameMessage) artworkError.focus({ preventScroll: true });
        }
        artworkHost?.classList.add('has-error');
        if (!isSameMessage) artworkHost?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      };
      const showArtworkUploadFailure = (reason = '') => {
        const compactReason = String(reason || '').replace(/\s+/g, ' ').trim();
        artworkUploadFailureReason = !compactReason || /^upload failed\.?$/i.test(compactReason)
          ? 'The upload service could not complete the file transfer.'
          : compactReason;
        const punctuation = /[.!?]$/.test(artworkUploadFailureReason) ? '' : '.';
        setArtworkStatus('error');
        showArtworkError(`Upload failed: ${artworkUploadFailureReason}${punctuation} Please try again or email it to orders@11packaging.com.`);
      };
      const resetArtworkFailureCandidate = () => {
        artworkFailureCandidate = '';
        artworkFailureCandidateSince = 0;
      };
      const normalizeOptionLabel = (value) => String(value || '').replace(/^_+/, '').trim().toLowerCase();
      const findYmqField = (label) => {
        const normalizedLabel = normalizeOptionLabel(label);
        return [...root.querySelectorAll('[ymq-option-label]')].find((field) => (
          normalizeOptionLabel(field.getAttribute('ymq-option-label')) === normalizedLabel
        )) || [...document.querySelectorAll('[ymq-option-label]')].find((field) => (
          normalizeOptionLabel(field.getAttribute('ymq-option-label')) === normalizedLabel
        ));
      };

      const optionUnitSurchargeCents = (select) => {
        const cents = Number(selectedOption(select)?.dataset.unitSurchargeCents || 0);
        return Number.isFinite(cents) ? cents : 0;
      };

      const selectedUnitSurchargeCents = () => (
        [...priceOptionSelects, artworkMethod].reduce((sum, select) => sum + optionUnitSurchargeCents(select), 0)
      );

      const syncPricingProperties = () => {
        const productForm = root.querySelector('form.elevenp-tp-form');
        if (!productForm) return;
        productForm.querySelectorAll('[data-11p-pricing-property]').forEach((input) => input.remove());
        priceOptionSelects.forEach((select) => {
          const propertyName = select.dataset.propertyName;
          const propertyValue = selectedOption(select)?.dataset.valueLabel || selectedOption(select)?.textContent?.trim();
          if (!propertyName || !propertyValue) return;
          if (findYmqField(propertyName)?.querySelector('select')) return;
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = `properties[${propertyName}]`;
          input.value = propertyValue;
          input.setAttribute('data-11p-pricing-property', 'true');
          productForm.append(input);
        });
      };

      const syncYmqPriceOptions = () => {
        let allReady = true;
        priceOptionSelects.forEach((select) => {
          const field = findYmqField(select.dataset.propertyName);
          const sourceSelect = field?.querySelector('select');
          const valueLabel = selectedOption(select)?.dataset.valueLabel || selectedOption(select)?.textContent?.trim();
          if (!field || !sourceSelect || !valueLabel) {
            allReady = false;
            return;
          }

          const desiredOption = [...sourceSelect.options].find((option) => (
            normalizeOptionLabel(option.value) === normalizeOptionLabel(valueLabel)
            || normalizeOptionLabel(option.textContent) === normalizeOptionLabel(valueLabel)
          ));
          if (!desiredOption) {
            allReady = false;
            return;
          }

          if (sourceSelect.value !== desiredOption.value) {
            sourceSelect.value = desiredOption.value;
            sourceSelect.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
        return allReady;
      };

      const connectYmqAdditional = () => {
        const productForm = root.querySelector('form.elevenp-tp-form');
        const field = findYmqField('Additional');
        const source = field?.closest('ymq-option');
        const input = field?.querySelector('input[id^="ymq-option-value"], input[type="text"], input');
        if (!source || !field || !input) return false;

        // Artwork may observe this same option set; repeated class writes retrigger it.
        if (!source.classList.contains('elevenp-ymq-source')) source.classList.add('elevenp-ymq-source');
        if (!field.classList.contains('elevenp-ymq-pricing-source')) field.classList.add('elevenp-ymq-pricing-source');
        field.setAttribute('ymq-option-label', '_Additional');
        field.setAttribute('aria-hidden', 'true');
        input.setAttribute('tabindex', '-1');
        ymqAdditionalInput = input;
        ymqAdditionalTotal = source.closest('form')?.querySelector('#ymq_addon_total')
          || productForm?.querySelector('#ymq_addon_total')
          || document.getElementById('ymq_addon_total');
        return true;
      };

      const syncYmqAdditional = (addOnCents) => {
        if (nativeMatrix) return addOnCents === 0;
        if (!connectYmqAdditional()) return addOnCents === 0;
        if (!syncYmqPriceOptions()) return false;
        if (lastSyncedAddOnCents === addOnCents) return true;
        if (addOnCents > 0 && !window.ymqOption?._setAttr) return false;

        const amount = Math.max(0, addOnCents) / 100;
        const value = amount.toFixed(2);
        ymqAdditionalInput.value = amount > 0 ? 'Configured options' : '';
        ymqAdditionalInput.setAttribute('add-price', value);
        ymqAdditionalTotal?.setAttribute('add-price', value);
        if (window.ymqOption?._setAttr) {
          window.ymqOption._setAttr(ymqAdditionalInput, 'price', amount);
          window.ymqOption._triggerInput?.(ymqAdditionalInput);
          if (ymqAdditionalTotal) {
            window.ymqOption._setAttr(ymqAdditionalTotal, 'price', amount);
            window.ymqOption._triggerInput?.(ymqAdditionalTotal);
          }
          window.ymqOption.buildFormData?.(ymqAdditionalInput.closest('ymq-option'));
        }
        lastSyncedAddOnCents = addOnCents;
        return true;
      };

      const selectedOptions = () => {
        const values = [];
        optionSelects.forEach((select) => {
          values[Number(select.dataset.optionIndex)] = select.value;
        });
        return values;
      };

      const selectedOptionsKey = () => selectedOptions().map((value, index) => `${index}:${value || ''}`).join('|');

      const readPersistedConfiguration = () => {
        try {
          const state = JSON.parse(window.sessionStorage.getItem(configurationStorageKey) || 'null');
          if (!state || state.version !== 1 || Date.now() - Number(state.savedAt || 0) > 30 * 60 * 1000) return null;
          return state;
        } catch (_) {
          return null;
        }
      };

      const persistConfiguration = () => {
        if (restoringConfiguration) return;
        const previous = readPersistedConfiguration();
        const selectedTier = rows.find((row) => row.querySelector('input[type="radio"]')?.checked)?.dataset.tierValue || '';
        const state = {
          version: 1,
          savedAt: Date.now(),
          nativeOptions: optionSelects.map((select) => ({
            index: select.dataset.optionIndex,
            value: select.value,
          })),
          priceOptions: priceOptionSelects.map((select, index) => ({ index, value: select.value })),
          compoundPrint: compoundPrint?.value || '',
          compoundLid: compoundLid?.value || '',
          cup002LidType: cup002LidType?.value || '',
          cup002LidColor: cup002LidColor?.value || '',
          bag001Parts: bag001CompoundParts.map((select) => select?.value || ''),
          bag004PrintSides: bag004PrintSides?.value || '',
          bag004PrintColors: bag004PrintColors?.value || '',
          bag004Valve: bag004Valve?.value || '',
          configuredMatrixParts: configuredMatrixParts.map((select) => select?.value || ''),
          tierValue: selectedTier,
          pricing: previous?.pricing || null,
        };
        if (nativeMatrix && nativePricingReady && variants.length) {
          state.pricing = {
            optionsKey: selectedOptionsKey(),
            variants,
          };
        }
        try {
          window.sessionStorage.setItem(configurationStorageKey, JSON.stringify(state));
        } catch (_) {
          // Browser form restoration remains the fallback when session storage is unavailable.
        }
      };

      const storedPricingIsValid = (pricing) => (
        !sparseQuantityTiers
        && pricing?.optionsKey === selectedOptionsKey()
        && Array.isArray(pricing.variants)
        && pricing.variants.length > 0
        && pricing.variants.every((variant) => (
          variant?.id
          && Number.isFinite(variant.price)
          && typeof variant.available === 'boolean'
          && Array.isArray(variant.options)
        ))
      );

      const findVariant = (tierValue) => {
        if (cup018NativeMatrix && cup018SizeFromOption(tierValue) !== cup018SizeSelect?.value) return null;
        const optionValues = selectedOptions();
        optionValues[quantityIndex] = tierValue;
        return variants.find((variant) => optionValues.every((value, index) => !value || variant.options[index] === value));
      };

      const syncSubmit = () => {
        const selectedRow = rows.find((row) => row.classList.contains('is-selected'));
        const addOnCents = selectedRow?._elevenpAddOnCents || 0;
        const pricingReady = nativePricingReady && syncYmqAdditional(addOnCents);
        const canSubmit = Boolean(selectedVariant?.available && selectedVariant?.id && pricingReady
          && cup002LidSelectionIsValid() && bag004SelectionIsValid());
        if (submitButton) submitButton.disabled = !canSubmit;
        if (variantInput) variantInput.disabled = !canSubmit;
        if (priceUnavailable) {
          if (nativeMatrix) {
            priceUnavailable.textContent = nativePricingLoading ? 'Updating price…' : priceUnavailableMessage;
            priceUnavailable.setAttribute('role', nativePricingLoading ? 'status' : 'alert');
          }
          priceUnavailable.hidden = pricingReady;
        }
      };

      const selectRow = (row) => {
        rows.forEach((candidate) => candidate.classList.remove('is-selected'));
        row?.classList.add('is-selected');
        const radio = row?.querySelector('input[type="radio"]');
        if (radio) radio.checked = true;
        selectedVariant = row?._elevenpVariant || null;
        if (cup018NativeMatrix && cup018SizeQuantitySelect && row) {
          cup018SizeQuantitySelect.value = row.dataset.tierValue;
        }

        if (variantInput) {
          // YMQ watches this ID even while the native pricing request disables the form.
          // Keep the last real CUP004 ID during loading; never dispatch an empty variant.
          const nextVariantId = selectedVariant?.id
            ? String(selectedVariant.id)
            : root.dataset.nativeMatrix === 'cup004' ? variantInput.value : '';
          const changed = variantInput.value !== nextVariantId;
          variantInput.value = nextVariantId;
          if (changed && !bag004NativeMatrix) {
            variantInput.dispatchEvent(new Event('input', { bubbles: true }));
            variantInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
        const quantityValue = parseQuantity(row?.dataset.tierValue);
        if (productionQuantity) {
          productionQuantity.value = quantityValue || row?.dataset.tierValue || '';
          productionQuantity.dispatchEvent(new CustomEvent('11p:quantity-change', {
            bubbles: true,
            detail: { quantity: quantityValue },
          }));
        }
        const displayedPrice = row?._elevenpFinalPrice;
        const quantityLabel = quantityValue
          ? `${new Intl.NumberFormat(locale || 'en-US').format(quantityValue)} units`
          : '—';
        const totalLabel = displayedPrice != null ? formatCurrency(displayedPrice, currency, locale) : '—';
        if (totalQuantity) totalQuantity.textContent = quantityLabel;
        if (total) total.textContent = totalLabel;
        stepQuantitySummaries.forEach((summary) => { summary.textContent = quantityLabel; });
        stepTotalSummaries.forEach((summary) => { summary.textContent = totalLabel; });
        root.dispatchEvent(new CustomEvent('11p:order-amount-change', {
          bubbles: true,
          detail: { amountCents: displayedPrice || 0 },
        }));
        syncPricingProperties();
        syncSubmit();
      };

      const refreshTiers = () => {
        const tierData = rows.map((row) => {
          const variant = quantityIndex >= 0 ? findVariant(row.dataset.tierValue) : null;
          const quantity = parseQuantity(row.dataset.tierValue);
          const baseUnit = variant && quantity ? variant.price / quantity : null;
          return { row, variant, quantity, baseUnit };
        });
        const baselineUnit = tierData.find(({ variant, baseUnit }) => variant?.available && baseUnit != null)?.baseUnit;
        const unitSurchargeCents = nativeMatrix ? 0 : selectedUnitSurchargeCents();

        tierData.forEach(({ row, variant, quantity, baseUnit }, index) => {
          row._elevenpVariant = variant;
          row.classList.toggle('is-unavailable', !variant?.available);
          row.querySelector('input').disabled = !variant?.available;
          const saving = baselineUnit && baseUnit != null && index > 0
            ? Math.max(0, Math.round(((baselineUnit - baseUnit) / baselineUnit) * 100))
            : 0;
          const discountFactor = (100 - saving) / 100;
          const addOnCents = variant && quantity
            ? Math.round(unitSurchargeCents * quantity * discountFactor)
            : 0;
          const displayedPrice = variant ? variant.price + addOnCents : null;
          row._elevenpAddOnCents = addOnCents;
          row._elevenpFinalPrice = displayedPrice;
          row.querySelector('[data-11p-tier-total]').textContent = displayedPrice != null ? formatCurrency(displayedPrice, currency, locale) : '—';
          row.querySelector('[data-11p-tier-unit]').textContent = displayedPrice != null && quantity
            ? `${formatUnitPrice(displayedPrice, quantity, currency, locale)}/unit`
            : '—';
          row.querySelector('[data-11p-tier-save]').textContent = saving > 0 ? `${saving}%` : '—';
        });

        refreshQuotedQuantityRows(root, rows);
        const checked = rows.find((row) => row.querySelector('input').checked && row._elevenpVariant?.available);
        selectRow(checked || rows.find((row) => row._elevenpVariant?.available));
      };

      rows.forEach((row) => {
        row.addEventListener('click', () => {
          if (!row._elevenpVariant?.available) return;
          selectRow(row);
          persistConfiguration();
        });
      });
      // Load one configuration's batch tiers while validating each product's complete matrix.
      const readNativeTiers = (payload, requestedOptions) => {
        const isCup011 = root.dataset.nativeMatrix === 'cup011';
        const isCup002 = root.dataset.nativeMatrix === 'cup002' || isCup011;
        const isCup004 = root.dataset.nativeMatrix === 'cup004';
        const isCup018 = cup018NativeMatrix;
        const isBag001 = root.dataset.nativeMatrix === 'bag001';
        const isBag006 = root.dataset.nativeMatrix === 'bag006';
        const isBag008 = root.dataset.nativeMatrix === 'bag008';
        const isBag009 = root.dataset.nativeMatrix === 'bag009';
        const isBag010 = root.dataset.nativeMatrix === 'bag010';
        const isBag004 = root.dataset.nativeMatrix === 'bag004';
        let configuredOptionCounts = [];
        try { configuredOptionCounts = JSON.parse(root.dataset.expectedOptionCounts || '[]'); } catch (_) { configuredOptionCounts = []; }
        const expectedVariantCount = (configuredMatrix || sparseQuantityTiers || isCup018) ? Number(root.dataset.expectedVariantCount) : isBag001 ? 1820 : isBag006 ? 160 : isBag008 ? 384 : isBag009 ? 1280 : isBag010 ? 1053 : isBag004 ? 240 : isCup011 ? 295 : isCup002 ? 335 : isCup004 ? 1440 : 1152;
        const expectedOptionCounts = (configuredMatrix || sparseQuantityTiers || isCup018) ? configuredOptionCounts : isBag001 ? [5, 7, 52] : isBag006 ? [5, 8, 4] : isBag008 ? [4, 4, 24] : isBag009 ? [5, 4, 64] : isBag010 ? [13, 9, 9] : isBag004 ? [6, 5, 8] : isCup011 ? [8, 8, 5] : isCup002 ? [8, 9, 5] : isCup004 ? [5, 8, 36] : [6, 8, 24];
        const configuredCartesianCount = configuredMatrix
          ? expectedOptionCounts.reduce((product, count) => product * Number(count || 0), 1)
          : expectedVariantCount;
        const expandedCup001Matrix = root.dataset.nativeMatrix === 'cup001'
          && payload?.variantCount === 1728
          && Array.isArray(payload.optionCounts) && payload.optionCounts.length === 3
          && [6, 8, 36].every((count, index) => payload.optionCounts[index] === count);
        const expectedQuantities = (configuredMatrix || sparseQuantityTiers || isCup018)
          ? rows.map((row) => row.dataset.tierValue)
          : isBag001
          ? ['50 pcs', '100 pcs', '200 pcs', '500 pcs', '1,000 pcs', '2,000 pcs', '5,000 pcs']
          : isBag006
            ? ['500', '1000', '2000', '5000', '10000', '15000', '20000', '30000']
          : isBag008 || isBag009
            ? ['500', '1000', '2000', '5000']
          : isBag010
            ? ['100', '200', '300', '500', '1000', '2000', '3000', '5000', '10000']
          : isBag004
            ? ['500', '1000', '2000', '3000', '5000']
            : ['1000', '2000', '3000', '5000', '8000', '10000', '15000', '20000'];
        if (isCup002 && !isCup011 && !sparseQuantityTiers) expectedQuantities.push('50000');
        const allowedQuantities = sparseQuantityTiers
          ? allowedQuantityTiers(quantityTierMatrix, requestedOptions, quantityIndex, expectedQuantities)
          : null;
        if ((configuredMatrix && !sparseQuantityTiers && configuredCartesianCount !== expectedVariantCount)
          || (sparseQuantityTiers && (!allowedQuantities
            || Number(quantityTierMatrix.expected_variant_count) !== expectedVariantCount
            || !Number.isInteger(expectedVariantCount) || expectedVariantCount < 1
            || expectedVariantCount > expectedOptionCounts.reduce((count, value) => count * Number(value || 0), 1)))
          || String(payload?.productId) !== root.dataset.productId
          || (!expandedCup001Matrix && (payload.variantCount !== expectedVariantCount
            || !Array.isArray(payload.optionCounts) || payload.optionCounts.length !== 3
            || !expectedOptionCounts.every((count, index) => payload.optionCounts[index] === count)))
          || !Array.isArray(payload.tiers) || payload.tiers.length !== expectedQuantities.length || rows.length !== expectedQuantities.length
          || !Array.isArray(payload.options) || payload.options.length !== 3 || requestedOptions.length !== 3
          || !requestedOptions.every((value, index) => payload.options[index] === value)) {
          throw new Error('The returned pricing configuration does not match the selection.');
        }
        const seenQuantities = new Set();
        return payload.tiers.map((tier) => {
          const row = rows.find((candidate) => candidate.dataset.tierValue === tier.quantityValue);
          if (!row || !expectedQuantities.includes(tier.quantityValue) || seenQuantities.has(tier.quantityValue)
            || String(tier.optionValueId) !== row.dataset.optionValueId) {
            throw new Error('The returned quantity tiers are incomplete.');
          }
          seenQuantities.add(tier.quantityValue);
          const variant = tier.variant;
          if (isCup002 && isUnsupportedCup002Quantity('cup002', requestedOptions[0], tier.quantityValue)) {
            if (variant !== null) throw new Error('An unsupported quantity unexpectedly has a price.');
            return null;
          }
          if (sparseQuantityTiers && !allowedQuantities.has(tier.quantityValue)) {
            if (variant !== null) throw new Error('An unquoted quantity unexpectedly has a variant.');
            return null;
          }
          if (!variant?.id || !Number.isFinite(variant.price) || variant.price <= 0
            || !Array.isArray(variant.options) || typeof variant.available !== 'boolean'
            || variant.options[quantityIndex] !== tier.quantityValue
            || !requestedOptions.every((value, index) => index === quantityIndex || variant.options[index] === value)) {
            throw new Error('The returned variant does not match its quantity tier.');
          }
          return variant;
        }).filter((variant) => variant !== null);
      };

      const clearNativePrices = () => {
        nativePricingReady = false;
        nativePricingLoading = false;
        variants = [];
        refreshTiers();
      };

      const loadNativeTiers = async () => {
        const sequence = ++nativeRequestSequence;
        nativePriceRequest?.abort();
        nativePriceRequest = new AbortController();
        const requestedOptions = selectedOptions();
        const allowedQuantities = sparseQuantityTiers
          ? allowedQuantityTiers(quantityTierMatrix, requestedOptions, quantityIndex, rows.map((row) => row.dataset.tierValue))
          : null;
        const supportedRows = cup018NativeMatrix
          ? rows.filter((row) => cup018SizeFromOption(row.dataset.tierValue) === cup018SizeSelect?.value)
          : sparseQuantityTiers
          ? rows.filter((row) => allowedQuantities?.has(row.dataset.tierValue))
          : ['cup002', 'cup011'].includes(root.dataset.nativeMatrix)
            ? rows.filter((row) => !isUnsupportedCup002Quantity('cup002', requestedOptions[0], row.dataset.tierValue))
            : rows;
        const selectedTier = supportedRows.find((row) => row.querySelector('input').checked) || supportedRows[0];
        const optionValueIds = [];
        optionSelects.forEach((select) => {
          optionValueIds[Number(select.dataset.optionIndex)] = selectedOption(select)?.dataset.optionValueId;
        });
        requestedOptions[quantityIndex] = selectedTier?.dataset.tierValue;
        optionValueIds[quantityIndex] = selectedTier?.dataset.optionValueId;
        const fallbackVariants = variants;
        const fallbackMatchesSelection = fallbackVariants.length > 0 && fallbackVariants.every((variant) => (
          requestedOptions.every((value, index) => index === quantityIndex || !value || variant.options?.[index] === value)
        ));
        nativePricingReady = false;
        nativePricingLoading = true;
        syncSubmit();
        try {
          if (optionValueIds.length !== 3 || optionValueIds.some((value) => !value)
            || !requestedOptions.every(Boolean)) throw new Error('Choose a supported configuration.');
          const url = new URL(root.dataset.productUrl, window.location.origin);
          const previewThemeId = new URL(window.location.href).searchParams.get('preview_theme_id');
          if (previewThemeId) url.searchParams.set('preview_theme_id', previewThemeId);
          url.searchParams.delete('variant');
          url.searchParams.set('section_id', root.dataset.sectionId);
          url.searchParams.set('option_values', optionValueIds.join(','));
          const response = await fetch(url, { signal: nativePriceRequest.signal, credentials: 'same-origin' });
          if (!response.ok) throw new Error('Prices could not be loaded.');
          const html = await response.text();
          if (sequence !== nativeRequestSequence || !root.isConnected) return;
          const documentFragment = new DOMParser().parseFromString(html, 'text/html');
          const payloadNode = documentFragment.querySelector('[data-11p-tp-native-tiers]');
          const payload = JSON.parse(payloadNode?.textContent || 'null');
          variants = readNativeTiers(payload, requestedOptions);
          nativePricingReady = true;
          nativePricingLoading = false;
          refreshTiers();
          persistConfiguration();
        } catch (error) {
          if (error.name === 'AbortError' || sequence !== nativeRequestSequence) return;
          nativePricingLoading = false;
          if (fallbackMatchesSelection) {
            variants = fallbackVariants;
            nativePricingReady = true;
            refreshTiers();
          } else {
            clearNativePrices();
          }
        }
      };

      const restoreConfiguration = () => {
        const state = readPersistedConfiguration();
        restoringConfiguration = true;

        if (state) {
          state.nativeOptions?.forEach(({ index, value }) => {
            const select = optionSelects.find((candidate) => candidate.dataset.optionIndex === String(index));
            if (select && [...select.options].some((option) => option.value === value)) select.value = value;
          });
          state.priceOptions?.forEach(({ index, value }) => {
            const select = priceOptionSelects[Number(index)];
            if (select && [...select.options].some((option) => option.value === value)) select.value = value;
          });
          if (compoundPrint && [...compoundPrint.options].some((option) => option.value === state.compoundPrint)) {
            compoundPrint.value = state.compoundPrint;
          }
          if (compoundLid && [...compoundLid.options].some((option) => option.value === state.compoundLid)) {
            compoundLid.value = state.compoundLid;
          }
          if (cup002LidType && [...cup002LidType.options].some((option) => option.value === state.cup002LidType)) {
            cup002LidType.value = state.cup002LidType;
          }
          if (cup002LidColor && [...cup002LidColor.options].some((option) => option.value === state.cup002LidColor)) {
            cup002LidColor.value = state.cup002LidColor;
          }
          if (Array.isArray(state.bag001Parts)) {
            bag001CompoundParts.forEach((select, index) => {
              const value = state.bag001Parts[index];
              if (select && [...select.options].some((option) => option.value === value)) select.value = value;
            });
          }
          if (Array.isArray(state.configuredMatrixParts)) {
            configuredMatrixParts.forEach((select, index) => {
              const value = state.configuredMatrixParts[index];
              if (select && [...select.options].some((option) => option.value === value)) select.value = value;
            });
          }
          if (bag004PrintSides && [...bag004PrintSides.options].some((option) => option.value === state.bag004PrintSides)) {
            bag004PrintSides.value = state.bag004PrintSides;
          }
          if (bag004PrintColors && [...bag004PrintColors.options].some((option) => option.value === state.bag004PrintColors)) {
            bag004PrintColors.value = state.bag004PrintColors;
          }
          if (bag004Valve && [...bag004Valve.options].some((option) => option.value === state.bag004Valve)) {
            bag004Valve.value = state.bag004Valve;
          }

          if (state.tierValue) {
            rows.forEach((row) => {
              const radio = row.querySelector('input[type="radio"]');
              if (radio) radio.checked = row.dataset.tierValue === String(state.tierValue);
            });
          }
          if (nativeMatrix && storedPricingIsValid(state.pricing)) {
            variants = state.pricing.variants;
            nativePricingReady = true;
          }
        }

        // Browsers may restore visible controls after this script's first paint.
        // Rebuild the hidden native option values from whichever controls are current.
        if (compoundSelect && compoundPrint && compoundLid) {
          const compoundValue = `${compoundPrint.value || ''} / ${compoundLid.value || ''}`;
          const compoundOption = [...compoundSelect.options].find((option) => option.value === compoundValue);
          if (compoundOption) compoundSelect.value = compoundOption.value;
        }
        if (cup002NativeMatrix && cup002NativeLid && cup002LidType && cup002LidColor) {
          const lidValue = cup002NativeLidValue(cup002LidType.value, cup002LidColor.value);
          const lidOption = [...cup002NativeLid.options].find((option) => option.value === lidValue);
          if (lidOption) cup002NativeLid.value = lidOption.value;
        }
        refreshBag001CompoundOptions();
        refreshConfiguredMatrixOptions();
        if (bag001NativeMatrix && compoundSelect && bag001CompoundParts.length) {
          const value = bag001CompoundParts.map((select) => select.value || '').join(bagCompoundSeparator);
          const option = [...compoundSelect.options].find((candidate) => candidate.value === value);
          if (option) compoundSelect.value = option.value;
        }
        if (bag004NativeMatrix && bag004NativeCombo && bag004PrintSides && bag004PrintColors && bag004Valve) {
          const value = bag004NativeComboValue(bag004PrintSides.value, bag004PrintColors.value, bag004Valve.value);
          const option = [...bag004NativeCombo.options].find((candidate) => candidate.value === value);
          if (option) bag004NativeCombo.value = option.value;
        }
        if (configuredMatrix && configuredMatrixOption && configuredMatrixParts.length) {
          const value = configuredNativeValue(configuredMatrixParts.map((select) => select.value || ''));
          const option = [...configuredMatrixOption.options].find((candidate) => candidate.value === value);
          if (option) configuredMatrixOption.value = option.value;
        }

        if (cup018NativeMatrix && cup018SizeSelect && cup018SizeQuantitySelect) {
          cup018SizeSelect.value = cup018SizeFromOption(cup018SizeQuantitySelect.value);
        }
        [...optionSelects, ...priceOptionSelects, compoundPrint, compoundLid, ...bag001CompoundParts, cup002LidType, cup002LidColor,
          bag004PrintSides, bag004PrintColors, bag004Valve, ...configuredMatrixParts, cup018SizeSelect]
          .filter(Boolean)
          .forEach((select) => select._elevenpRefreshSelect?.());
        refreshTiers();
        restoringConfiguration = false;

        if (nativeMatrix) loadNativeTiers();
        return Boolean(state);
      };

      if (nativeMatrix) {
        const syncCompoundSelection = () => {
          refreshBag001CompoundOptions();
          const value = bag001NativeMatrix
            ? bag001CompoundParts.map((select) => select?.value || '').join(bagCompoundSeparator)
            : `${compoundPrint?.value || ''} / ${compoundLid?.value || ''}`;
          const option = [...(compoundSelect?.options || [])].find((candidate) => candidate.value === value);
          if (compoundSelect) compoundSelect.value = option?.value || '';
          loadNativeTiers();
        };
        const compoundControllers = bag001NativeMatrix ? bag001CompoundParts : [compoundPrint, compoundLid];
        refreshBag001CompoundOptions();
        compoundControllers.forEach((select) => select?.addEventListener('change', syncCompoundSelection));
        optionSelects.forEach((select) => select.addEventListener('change', () => {
          if (configuredMatrix && mappedPresentation && select !== configuredMatrixOption) {
            refreshConfiguredMatrixOptions();
            configuredMatrixOption.value = configuredNativeValue(configuredMatrixParts.map((part) => part.value || ''));
          }
          loadNativeTiers();
        }));
        if (cup018NativeMatrix && cup018SizeSelect && cup018SizeQuantitySelect) {
          cup018SizeSelect.addEventListener('change', () => {
            const currentRow = rows.find((row) => row.querySelector('input[type="radio"]')?.checked);
            const quantity = parseQuantity(currentRow?.dataset.tierValue || cup018SizeQuantitySelect.value);
            const supportedRows = rows.filter((row) => cup018SizeFromOption(row.dataset.tierValue) === cup018SizeSelect.value);
            const matchingRow = supportedRows.find((row) => parseQuantity(row.dataset.tierValue) === quantity) || supportedRows[0];
            if (!matchingRow) return;
            cup018SizeQuantitySelect.value = matchingRow.dataset.tierValue;
            rows.forEach((row) => { row.querySelector('input[type="radio"]').checked = row === matchingRow; });
            loadNativeTiers();
          });
        }
        if (configuredMatrix) {
          const syncConfiguredMatrixSelection = () => {
            refreshConfiguredMatrixOptions();
            const value = configuredNativeValue(configuredMatrixParts.map((select) => select?.value || ''));
            const option = [...(configuredMatrixOption?.options || [])].find((candidate) => candidate.value === value);
            if (configuredMatrixOption) configuredMatrixOption.value = option?.value || '';
            loadNativeTiers();
          };
          refreshConfiguredMatrixOptions();
          configuredMatrixParts.forEach((select) => select?.addEventListener('change', syncConfiguredMatrixSelection));
        }
        if (cup002NativeMatrix) {
          const syncCup002LidSelection = () => {
            const noLid = cup002LidType?.value === 'None';
            if (cup002LidColor) {
              cup002LidColor.disabled = noLid;
              if (noLid) cup002LidColor.value = '';
              else if (!['White', 'Black'].includes(cup002LidColor.value)) cup002LidColor.value = 'White';
              cup002LidColor._elevenpRefreshSelect?.();
            }
            const value = cup002NativeLidValue(cup002LidType?.value, cup002LidColor?.value);
            const option = [...(cup002NativeLid?.options || [])].find((candidate) => candidate.value === value);
            if (cup002NativeLid) cup002NativeLid.value = option?.value || '';
            loadNativeTiers();
          };
          [cup002LidType, cup002LidColor].forEach((select) => select?.addEventListener('change', syncCup002LidSelection));
        }
        if (bag004NativeMatrix) {
          const syncBag004Selection = () => {
            const value = bag004NativeComboValue(bag004PrintSides?.value, bag004PrintColors?.value, bag004Valve?.value);
            const option = [...(bag004NativeCombo?.options || [])].find((candidate) => candidate.value === value);
            if (bag004NativeCombo) bag004NativeCombo.value = option?.value || '';
            loadNativeTiers();
          };
          [bag004PrintSides, bag004PrintColors, bag004Valve]
            .forEach((select) => select?.addEventListener('change', syncBag004Selection));
        }
        root.querySelector('form.elevenp-tp-form')?.addEventListener('submit', (event) => {
          if (!nativePricingReady || !selectedVariant?.available || variantInput?.disabled
            || !cup002LidSelectionIsValid() || !bag001ConfigurationIsValid() || !bag004SelectionIsValid()
            || !configuredMatrixSelectionIsValid()) {
            event.preventDefault();
            event.stopImmediatePropagation();
          }
        }, true);
        try {
          const payload = JSON.parse(root.querySelector('[data-11p-tp-native-tiers]')?.textContent || 'null');
          const initialOptions = selectedOptions();
          initialOptions[quantityIndex] = rows.find((row) => row.querySelector('input').checked)?.dataset.tierValue || rows[0]?.dataset.tierValue;
          variants = readNativeTiers(payload, initialOptions);
          nativePricingReady = true;
        } catch (_) {
          clearNativePrices();
        }
      } else {
        optionSelects.forEach((select) => select.addEventListener('change', refreshTiers));
      }
      priceOptionSelects.forEach((select) => select.addEventListener('change', refreshTiers));

      [...new Set([
        ...optionSelects,
        ...priceOptionSelects,
        compoundPrint,
        compoundLid,
        cup002LidType,
        cup002LidColor,
        bag004PrintSides,
        bag004PrintColors,
        bag004Valve,
        cup018SizeSelect,
        ...configuredMatrixParts,
      ].filter(Boolean))].forEach((select) => select.addEventListener('change', persistConfiguration));
      root.querySelector('form.elevenp-tp-form')?.addEventListener('submit', persistConfiguration, true);

      const tierToggle = root.querySelector('[data-11p-tier-toggle]');
      const otherTier = root.querySelector('[data-11p-other-tier]');
      tierToggle?.addEventListener('click', () => {
        const expanded = tierToggle.getAttribute('aria-expanded') === 'true';
        if (expanded) {
          const selectedExtraRow = rows.find((row) => (
            row.classList.contains('is-extra') && row.querySelector('input[type="radio"]')?.checked
          ));
          if (selectedExtraRow) {
            selectRow(rows.find((row) => !row.classList.contains('is-extra') && row._elevenpVariant?.available));
          }
        }
        tierToggle.setAttribute('aria-expanded', String(!expanded));
        tierToggle.querySelector('span').textContent = expanded ? 'Show more quantities' : 'Show fewer quantities';
        refreshQuotedQuantityRows(root, rows);
        if (otherTier) otherTier.hidden = expanded;
      });

      root.querySelector('[data-11p-other-tier-submit]')?.addEventListener('click', () => {
        const quantity = Number(root.querySelector('[data-11p-other-tier-input]')?.value || 0);
        const quoteLink = root.querySelector('.elevenp-quote-fallback a')?.href;
        if (!quantity || !quoteLink) return;
        const url = new URL(quoteLink, window.location.origin);
        url.searchParams.set('quantity', String(quantity));
        url.searchParams.set('product', document.title);
        window.location.assign(url.toString());
      });

      const optionForArtworkMode = (select, mode) => [...(select?.options || [])].find((option) => {
        const label = `${option.value} ${option.textContent}`.toLowerCase();
        return mode === 'email_later' ? label.includes('email') : label.includes('upload');
      });

      const normalizeArtworkReference = (value) => {
        try {
          const url = new URL(String(value || '').trim());
          const approvedHost = url.hostname === 'cdn.shopify.com'
            || url.hostname === 'cdn.ymqapp.com'
            || url.hostname.endsWith('.ymqapp.com');
          if (url.protocol !== 'https:' || !approvedHost) return '';
          return url.toString();
        } catch (_) {
          return '';
        }
      };

      const readYmqArtworkReference = () => {
        const field = ymqArtwork?.querySelector('[ymq-option-label="Artwork file"]');
        if (!field) return '';
        const candidates = [
          field.querySelector('.ymq-option-title-right-name')?.textContent,
          field.querySelector('input.ymq-option-hidden-file-input')?.value,
          ...[...field.querySelectorAll('a[href]')].map((link) => link.href),
        ];
        return candidates.map(normalizeArtworkReference).find(Boolean) || '';
      };

      const readYmqArtworkFailureCandidate = () => {
        const field = ymqArtwork?.querySelector('[ymq-option-label="Artwork file"]');
        if (!field) return '';
        const messages = [
          ...field.querySelectorAll('.ymq-option-preview-item-error .ymq-option-preview-error'),
          field.querySelector('.ymq-option-error'),
        ].filter(Boolean).map((node) => node.textContent.replace(/\s+/g, ' ').trim()).filter(Boolean);
        return messages[0] || '';
      };

      const readStableYmqArtworkFailure = () => {
        const failure = readYmqArtworkFailureCandidate();
        if (!failure) {
          resetArtworkFailureCandidate();
          return '';
        }

        const now = Date.now();
        if (failure !== artworkFailureCandidate) {
          artworkFailureCandidate = failure;
          artworkFailureCandidateSince = now;
        }

        const selectionAge = artworkUploadStartedAt ? now - artworkUploadStartedAt : 0;
        const failureAge = now - artworkFailureCandidateSince;
        const transientValidation = /required|please upload|choose(?: an?)? file/i.test(failure);
        if (transientValidation || selectionAge < 2500 || failureAge < 1000) return '';
        return failure;
      };

      const readYmqArtworkProgress = () => {
        const field = ymqArtwork?.querySelector('[ymq-option-label="Artwork file"]');
        const values = [...(field?.querySelectorAll('.ymq-option-upload-percent') || [])]
          .map((node) => node.textContent.trim())
          .filter((value) => /^\d+%$/.test(value));
        return values.at(-1) || '';
      };

      const artworkReferenceFileName = (reference) => {
        try {
          return decodeURIComponent(new URL(reference).pathname.split('/').pop() || 'Artwork file');
        } catch (_) {
          return 'Artwork file';
        }
      };

      const syncYmqArtworkReference = () => {
        const reference = readYmqArtworkReference();
        if (reference) {
          artworkReference = reference;
          artworkSelectedName = artworkSelectedName || artworkReferenceFileName(reference);
          artworkUploadFailureReason = '';
          artworkUploadStartedAt = 0;
          resetArtworkFailureCandidate();
          stopArtworkUploadMonitor();
        }

        const emailLater = artworkMethod?.value === 'email_later';
        const propertyInput = ymqArtwork?.querySelector(
          '[ymq-option-label="Artwork file"] input[name="properties[Artwork file]"]'
        ) || root.querySelector('input[name="properties[Artwork file]"]');
        if (propertyInput) propertyInput.value = emailLater ? '' : artworkReference;

        return artworkReference;
      };

      const observeYmqArtwork = (source) => {
        if (!source || observedYmqArtwork === source) return;
        ymqArtworkObserver?.disconnect();
        observedYmqArtwork = source;
        ymqArtworkObserver = new MutationObserver(() => {
          syncYmqArtworkReference();
          syncArtworkUi();
        });
        ymqArtworkObserver.observe(source, {
          attributes: true,
          childList: true,
          subtree: true,
          characterData: true,
          attributeFilter: ['class', 'disabled', 'style'],
        });
      };

      const syncArtworkUi = (mode = artworkMethod?.value || 'upload_now') => {
        const emailLater = mode === 'email_later';
        if (artworkUpload) artworkUpload.hidden = false;
        if (artworkEmailWrap) artworkEmailWrap.hidden = false;
        if (artworkFile) artworkFile.disabled = false;
        if (artworkHost) artworkHost.dataset.artworkMode = emailLater ? 'email_later' : 'upload_now';
        artworkEmail?.setAttribute('aria-pressed', String(emailLater));
        if (artworkProperty) {
          artworkProperty.value = selectedOption(artworkMethod)?.dataset.valueLabel
            || selectedOption(artworkMethod)?.textContent?.trim()
            || (emailLater ? 'Email Later' : 'Upload Now');
        }
        if (artworkFileName) {
          if (emailLater) {
            artworkFileName.textContent = 'or drag and drop';
            artworkUploadFailureReason = '';
            artworkUploadStartedAt = 0;
            resetArtworkFailureCandidate();
            stopArtworkUploadMonitor();
            setArtworkStatus('idle');
          } else if (artworkReference) {
            const uploadedFileName = artworkSelectedName || artworkReferenceFileName(artworkReference);
            artworkFileName.textContent = uploadedFileName;
            artworkUploadFailureReason = '';
            clearArtworkError();
            stopArtworkUploadMonitor();
            setArtworkStatus('success', `Upload successful: ${uploadedFileName}`);
          } else if (artworkSelectedName) {
            artworkFileName.textContent = artworkSelectedName;
            const uploadFailure = artworkUploadFailureReason || readStableYmqArtworkFailure();
            if (uploadFailure) {
              showArtworkUploadFailure(uploadFailure);
            } else {
              const progress = readYmqArtworkProgress();
              clearArtworkError();
              setArtworkStatus('uploading', progress ? `Uploading… ${progress}` : 'Preparing secure upload…');
            }
          } else {
            artworkFileName.textContent = artworkFile?.files?.[0]?.name || ymqUploadInput?.files?.[0]?.name || 'or drag and drop';
            setArtworkStatus('idle');
          }
        }
        if (emailLater || artworkReference) clearArtworkError();
        syncSubmit();
      };

      const connectYmqArtwork = () => {
        const productForm = root.querySelector('form.elevenp-tp-form');
        const source = [...(productForm?.querySelectorAll('ymq-option') || [])].find((element) => (
          element.querySelector('[ymq-option-label="Artwork"]')
        ));
        if (!source) return false;

        const methodSelect = source.querySelector('[ymq-option-label="Artwork"] select');
        if (!methodSelect) return false;

        ymqArtwork = source;
        ymqMethodSelect = methodSelect;
        ymqUploadInput = source.querySelector('[ymq-option-label="Artwork file"] input[type="file"]');
        ymqArtwork.classList.add('elevenp-ymq-source');
        observeYmqArtwork(source);

        const artworkSourceFields = [
          source.querySelector('[ymq-option-label="Artwork"]'),
          source.querySelector('[ymq-option-label="Artwork file"]'),
        ].filter(Boolean);
        artworkSourceFields.forEach((field) => {
          field.classList.add('elevenp-ymq-artwork-source');
          field.setAttribute('aria-hidden', 'true');
          field.querySelectorAll('button, input, select, textarea, a[href]').forEach((control) => {
            control.setAttribute('tabindex', '-1');
          });
        });

        const userSelectedMode = artworkMethod?.dataset.userSelected === 'true' ? artworkMethod.value : '';
        const mode = userSelectedMode
          || artworkMethod?.value
          || 'upload_now';
        const desiredOption = optionForArtworkMode(ymqMethodSelect, mode);
        if (desiredOption && ymqMethodSelect.value !== desiredOption.value) {
          ymqMethodSelect.value = desiredOption.value;
          ymqMethodSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }

        if (artworkMethod) {
          const uploadOption = optionForArtworkMode(artworkMethod, 'upload_now');
          if (uploadOption) uploadOption.disabled = false;
          artworkMethod.disabled = false;
          artworkMethod.value = mode;
          artworkMethod._elevenpRefreshSelect?.();
        }
        if (artworkProperty) artworkProperty.disabled = true;
        if (artworkFile) {
          artworkFile.removeAttribute('name');
          artworkFile.disabled = false;
        }
        if (artworkHost) {
          artworkHost.dataset.ymqReady = 'true';
          artworkHost.dataset.artworkState = 'ready';
          artworkHost.dataset.uploadMode = 'ymq';
        }
        const pendingArtworkFile = artworkFile?.files?.[0];
        if (pendingArtworkFile) {
          artworkSelectedName = pendingArtworkFile.name;
          const signature = `${pendingArtworkFile.name}:${pendingArtworkFile.size}:${pendingArtworkFile.lastModified}`;
          if (
            ymqUploadInput
            && !ymqUploadInput.disabled
            && (signature !== lastBridgedArtworkFileSignature || ymqUploadInput !== lastBridgedYmqUploadInput)
          ) {
            try {
              ymqUploadInput.files = artworkFile.files;
              lastBridgedArtworkFileSignature = signature;
              lastBridgedYmqUploadInput = ymqUploadInput;
              ymqUploadInput.dispatchEvent(new Event('change', { bubbles: true }));
            } catch (_) {
              showArtworkUploadFailure('This browser could not pass the selected file to the secure upload service.');
            }
          }
        }
        syncYmqArtworkReference();
        syncArtworkUi(mode);
        connectYmqAdditional();
        if (bag004NativeMatrix && variantInput && selectedVariant?.id) {
          variantInput.value = String(selectedVariant.id);
        }
        return true;
      };

      const retryYmqConnection = () => {
        [0, 100, 250, 500].forEach((delay) => window.setTimeout(connectYmqArtwork, delay));
      };

      const startArtworkUploadMonitor = () => {
        stopArtworkUploadMonitor();
        let attempts = 0;
        const monitorUpload = () => {
          if (!root.isConnected || !artworkSelectedName || artworkMethod?.value === 'email_later') {
            stopArtworkUploadMonitor();
            return;
          }
          attempts += 1;
          connectYmqArtwork();
          const reference = syncYmqArtworkReference();
          syncArtworkUi('upload_now');
          const uploadFailure = artworkUploadFailureReason || readStableYmqArtworkFailure();
          if (uploadFailure && !artworkUploadFailureReason) showArtworkUploadFailure(uploadFailure);
          if (reference || uploadFailure) {
            stopArtworkUploadMonitor();
            return;
          }
          if (attempts >= 180) {
            showArtworkUploadFailure('The upload service did not respond within 90 seconds.');
            stopArtworkUploadMonitor();
          }
        };
        monitorUpload();
        if (!artworkReference && !artworkUploadFailureReason) {
          artworkUploadMonitor = window.setInterval(monitorUpload, 500);
        }
      };

      if (artworkMethod) {
        const defaultEmailOption = optionForArtworkMode(artworkMethod, 'email_later');
        if (defaultEmailOption) artworkMethod.value = defaultEmailOption.value;
      }

      artworkMethod?.addEventListener('change', () => {
        artworkMethod.dataset.userSelected = 'true';
        connectYmqArtwork();
        const option = optionForArtworkMode(ymqMethodSelect, artworkMethod.value);
        if (option && ymqMethodSelect.value !== option.value) {
          ymqMethodSelect.value = option.value;
          ymqMethodSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }
        syncArtworkUi(artworkMethod.value);
        refreshTiers();
        retryYmqConnection();
      });

      artworkFile?.addEventListener('change', () => {
        artworkSelectedName = artworkFile.files?.[0]?.name || '';
        artworkReference = '';
        artworkUploadFailureReason = '';
        artworkUploadStartedAt = artworkSelectedName ? Date.now() : 0;
        resetArtworkFailureCandidate();
        lastBridgedArtworkFileSignature = '';
        lastBridgedYmqUploadInput = null;
        clearArtworkError();
        if (!artworkSelectedName) {
          if (artworkMethod) {
            const emailOption = optionForArtworkMode(artworkMethod, 'email_later');
            if (emailOption) artworkMethod.value = emailOption.value;
          }
          syncArtworkUi('email_later');
          return;
        }
        if (artworkSelectedName && artworkMethod) {
          const uploadOption = optionForArtworkMode(artworkMethod, 'upload_now');
          if (uploadOption) artworkMethod.value = uploadOption.value;
          artworkMethod.dataset.userSelected = 'true';
        }
        connectYmqArtwork();
        const uploadOption = optionForArtworkMode(ymqMethodSelect, 'upload_now');
        if (uploadOption && ymqMethodSelect.value !== uploadOption.value) {
          ymqMethodSelect.value = uploadOption.value;
          ymqMethodSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }
        syncArtworkUi('upload_now');
        startArtworkUploadMonitor();
      });

      root.querySelector('form.elevenp-tp-form')?.addEventListener('formdata', (event) => {
        if (bag004NativeMatrix && selectedVariant?.id) {
          event.formData.set('id', String(selectedVariant.id));
        }
        if (cup002NativeMatrix && cup002LidSelectionIsValid()) {
          event.formData.set('properties[Cup Size]', selectedOptions()[0]);
          event.formData.set('properties[Lid Type]', selectedOption(cup002LidType)?.textContent.trim() || cup002LidType.value);
          if (cup002LidType?.value === 'None') event.formData.delete('properties[Lid Color]');
          else event.formData.set('properties[Lid Color]', cup002LidColor.value);
        }
        syncYmqArtworkReference();
        const emailLater = artworkMethod?.value === 'email_later';
        const orderedCartProperties = [];
        const orderedProperties = [];
        const seenCartProperties = new Set();
        const sourcePropertyKeys = new Set();
        const addOrderedCartProperty = (label, key, value, { checkoutVisible = false } = {}) => {
          const cleanLabel = String(label || '').replace(/\s*\*\s*$/, '').trim();
          const cleanKey = String(key || '').trim();
          const cleanValue = String(value || '').trim();
          if (!cleanLabel || !cleanKey || !cleanValue || seenCartProperties.has(cleanKey)) return;
          const submittedKey = checkoutVisible ? cleanKey : `_11p_cart_${cleanKey}`;
          sourcePropertyKeys.add(cleanKey);
          orderedProperties.push({ key: submittedKey, value: cleanValue });
          orderedCartProperties.push(`${cleanLabel}::${submittedKey}`);
          seenCartProperties.add(cleanKey);
        };

        root.querySelectorAll('.elevenp-purchase-step--options .elevenp-field').forEach((field) => {
          const select = field.querySelector('select');
          const option = selectedOption(select);
          if (!select || !option || select.disabled) return;
          const propertyMatch = select.name.match(/^properties\[(.+)\]$/);
          const label = field.children[0]?.textContent || select.dataset.propertyName || '';
          const key = propertyMatch?.[1] || select.dataset.propertyName || label.replace(/\s*\*\s*$/, '').trim();
          const value = option.dataset.valueLabel || option.textContent.trim();
          addOrderedCartProperty(label, key, value);
        });

        addOrderedCartProperty('Quantity', 'Quantity', productionQuantity?.value);
        addOrderedCartProperty(
          'Artwork',
          'Artwork',
          !emailLater && artworkReference ? artworkReference : 'Upload Later',
          { checkoutVisible: true },
        );

        const replacedPropertyKeys = new Set([
          ...sourcePropertyKeys,
          ...orderedProperties.map(({ key }) => key),
          'Production quantity',
          'Quantity',
          'Artwork',
          'Artwork method',
          'Artwork file',
        ]);
        replacedPropertyKeys.forEach((key) => event.formData.delete(`properties[${key}]`));
        orderedProperties.forEach(({ key, value }) => event.formData.append(`properties[${key}]`, value));
        event.formData.delete('properties[_11p_cart_option_order]');
        event.formData.append('properties[_11p_cart_option_order]', orderedCartProperties.join('||'));
      });

      root._elevenpBridgeYmqArtwork = connectYmqArtwork;

      const validateArtworkBeforeSubmit = () => {
        const artworkReady = connectYmqArtwork();
        const emailLater = artworkMethod?.value === 'email_later';
        if (emailLater) {
          artworkUploadFailureReason = '';
          artworkUploadStartedAt = 0;
          resetArtworkFailureCandidate();
          stopArtworkUploadMonitor();
          syncArtworkUi('email_later');
          clearArtworkError();
          return true;
        }

        const reference = artworkReady ? syncYmqArtworkReference() : '';
        if (reference) {
          clearArtworkError();
          return true;
        }

        const fileSelected = Boolean(artworkSelectedName || artworkFile?.files?.length || ymqUploadInput?.files?.length);
        if (!fileSelected) {
          if (artworkMethod) {
            const emailOption = optionForArtworkMode(artworkMethod, 'email_later');
            if (emailOption) artworkMethod.value = emailOption.value;
          }
          const ymqEmailOption = optionForArtworkMode(ymqMethodSelect, 'email_later');
          if (ymqEmailOption && ymqMethodSelect.value !== ymqEmailOption.value) {
            ymqMethodSelect.value = ymqEmailOption.value;
            ymqMethodSelect.dispatchEvent(new Event('change', { bubbles: true }));
          }
          syncArtworkUi('email_later');
          clearArtworkError();
          return true;
        }

        showArtworkError('Please wait for your artwork to finish uploading, or use “Copy email address” to send it later.');
        return false;
      };

      productForm?.addEventListener('click', (event) => {
        const clickedElement = event.target instanceof Element ? event.target : null;
        const target = clickedElement?.closest('[type="submit"][name="add"]')
          || clickedElement?.querySelector('[type="submit"][name="add"]');
        if (!target || validateArtworkBeforeSubmit()) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      }, true);

      productForm?.addEventListener('submit', (event) => {
        if (validateArtworkBeforeSubmit()) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      }, true);

      const copyArtworkEmail = async (email) => {
        try {
          if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(email);
            return true;
          }
        } catch (_) {}

        const copyField = document.createElement('textarea');
        copyField.value = email;
        copyField.setAttribute('readonly', '');
        copyField.style.position = 'fixed';
        copyField.style.opacity = '0';
        document.body.appendChild(copyField);
        copyField.select();
        let copied = false;
        try {
          copied = document.execCommand('copy');
        } catch (_) {}
        copyField.remove();
        return copied;
      };

      artworkEmail?.addEventListener('click', async () => {
        const email = artworkEmail.dataset.email;
        if (!email) return;
        artworkUploadFailureReason = '';
        artworkUploadStartedAt = 0;
        resetArtworkFailureCandidate();
        stopArtworkUploadMonitor();
        if (artworkMethod) {
          const emailOption = optionForArtworkMode(artworkMethod, 'email_later');
          if (emailOption) artworkMethod.value = emailOption.value;
          artworkMethod.dataset.userSelected = 'true';
        }
        connectYmqArtwork();
        const emailOption = optionForArtworkMode(ymqMethodSelect, 'email_later');
        if (emailOption && ymqMethodSelect.value !== emailOption.value) {
          ymqMethodSelect.value = emailOption.value;
          ymqMethodSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }
        syncArtworkUi('email_later');
        refreshTiers();
        const copied = await copyArtworkEmail(email);
        if (artworkEmailLabel) artworkEmailLabel.textContent = copied ? 'Email address copied' : 'Copy unavailable';
        window.clearTimeout(artworkCopyFeedbackTimer);
        artworkCopyFeedbackTimer = window.setTimeout(() => {
          if (artworkEmailLabel) artworkEmailLabel.textContent = 'Copy email address';
        }, 2500);
        if (copied) {
          if (artworkCopyStatus) artworkCopyStatus.textContent = 'Email address copied. Email Later is selected for this order.';
        } else {
          if (artworkCopyStatus) artworkCopyStatus.textContent = 'Copy unavailable. Select the email address and copy it manually. Email Later is selected for this order.';
        }
      });

      root._elevenpRestoreConfiguration = restoreConfiguration;
      if (readPersistedConfiguration()) restoreConfiguration();
      else refreshTiers();
      connectYmqArtwork();
      if (!nativeMatrix) connectYmqAdditional();
      if (artworkHost) {
        let ymqPollAttempts = 0;
        const ymqPoll = window.setInterval(() => {
          ymqPollAttempts += 1;
          const artworkReady = connectYmqArtwork();
          const additionalReady = nativeMatrix || connectYmqAdditional();
          syncSubmit();
          if ((artworkReady && additionalReady) || ymqPollAttempts >= 40) {
            window.clearInterval(ymqPoll);
          }
        }, 250);
      }
    });
  };

  const deliveryGeoCacheKey = 'elevenp_delivery_geo_v1';
  const deliveryGeoCacheTtl = 24 * 60 * 60 * 1000;
  let deliveryGeoRequest;

  const getDeliveryGeo = async () => {
    try {
      const cached = JSON.parse(window.localStorage.getItem(deliveryGeoCacheKey) || 'null');
      if (cached?.timestamp && Date.now() - cached.timestamp < deliveryGeoCacheTtl) return cached;
    } catch (_) {
      // Continue without storage when the browser blocks localStorage.
    }

    if (!deliveryGeoRequest) {
      deliveryGeoRequest = (async () => {
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 1800);
        try {
          const response = await fetch(
            'https://ipwho.is/?fields=success,country,country_code,region,region_code,city,message',
            {
              credentials: 'omit',
              mode: 'cors',
              referrerPolicy: 'no-referrer',
              signal: controller.signal,
            },
          );
          if (!response.ok) return null;
          const data = await response.json();
          const countryCode = String(data?.country_code || '').trim().toUpperCase();
          const countryName = String(data?.country || '').trim();
          const region = String(data?.region || '').trim();
          const city = String(data?.city || '').trim();
          if (data?.success !== true || !countryCode || !countryName) return null;
          const result = { countryCode, countryName, region, city, timestamp: Date.now() };
          try {
            window.localStorage.setItem(deliveryGeoCacheKey, JSON.stringify(result));
          } catch (_) {
            // A blocked cache should not prevent the delivery estimator from working.
          }
          return result;
        } catch (_) {
          return null;
        } finally {
          window.clearTimeout(timeout);
        }
      })();
    }

    return deliveryGeoRequest;
  };

  const initDeliveryEstimators = (scope = document) => {
    const selector = '[data-11p-delivery-estimator]:not([data-11p-delivery-ready])';
    const estimators = [
      ...(scope.matches?.(selector) ? [scope] : []),
      ...scope.querySelectorAll(selector),
    ];

    const normalizeCountryName = (value) => String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/gi, ' ')
      .trim()
      .toLocaleLowerCase('en-US');

    const countryCodeAliases = {
      'bolivia': 'BO',
      'brunei': 'BN',
      'cape verde': 'CV',
      'czech republic': 'CZ',
      'democratic republic of the congo': 'CD',
      'iran': 'IR',
      'ivory coast': 'CI',
      'laos': 'LA',
      'moldova': 'MD',
      'north korea': 'KP',
      'palestinian territories': 'PS',
      'republic of the congo': 'CG',
      'russia': 'RU',
      'south korea': 'KR',
      'syria': 'SY',
      'taiwan': 'TW',
      'tanzania': 'TZ',
      'turkey': 'TR',
      'united states': 'US',
      'venezuela': 'VE',
      'vietnam': 'VN',
    };

    const countryCodeByName = new Map(Object.entries(countryCodeAliases));
    if (typeof Intl.DisplayNames === 'function') {
      const regionNames = new Intl.DisplayNames(['en-US'], { type: 'region' });
      for (let first = 65; first <= 90; first += 1) {
        for (let second = 65; second <= 90; second += 1) {
          const code = String.fromCharCode(first, second);
          const name = regionNames.of(code);
          if (name && name !== code) countryCodeByName.set(normalizeCountryName(name), code);
        }
      }
    }

    // ITU-T E.164 calling codes, keyed by ISO 3166-1 alpha-2 country/region code.
    const callingCodeByCountry = {
      AC: '247', AD: '376', AE: '971', AF: '93', AG: '1', AI: '1', AL: '355', AM: '374', AO: '244', AR: '54', AS: '1', AT: '43', AU: '61', AW: '297', AX: '358', AZ: '994',
      BA: '387', BB: '1', BD: '880', BE: '32', BF: '226', BG: '359', BH: '973', BI: '257', BJ: '229', BL: '590', BM: '1', BN: '673', BO: '591', BQ: '599', BR: '55', BS: '1', BT: '975', BW: '267', BY: '375', BZ: '501',
      CA: '1', CC: '61', CD: '243', CF: '236', CG: '242', CH: '41', CI: '225', CK: '682', CL: '56', CM: '237', CN: '86', CO: '57', CR: '506', CU: '53', CV: '238', CW: '599', CX: '61', CY: '357', CZ: '420',
      DE: '49', DJ: '253', DK: '45', DM: '1', DO: '1', DZ: '213', EC: '593', EE: '372', EG: '20', EH: '212', ER: '291', ES: '34', ET: '251', FI: '358', FJ: '679', FK: '500', FM: '691', FO: '298', FR: '33',
      GA: '241', GB: '44', GD: '1', GE: '995', GF: '594', GG: '44', GH: '233', GI: '350', GL: '299', GM: '220', GN: '224', GP: '590', GQ: '240', GR: '30', GT: '502', GU: '1', GW: '245', GY: '592',
      HK: '852', HN: '504', HR: '385', HT: '509', HU: '36', ID: '62', IE: '353', IL: '972', IM: '44', IN: '91', IO: '246', IQ: '964', IR: '98', IS: '354', IT: '39', JE: '44', JM: '1', JO: '962', JP: '81',
      KE: '254', KG: '996', KH: '855', KI: '686', KM: '269', KN: '1', KP: '850', KR: '82', KW: '965', KY: '1', KZ: '7', LA: '856', LB: '961', LC: '1', LI: '423', LK: '94', LR: '231', LS: '266', LT: '370', LU: '352', LV: '371', LY: '218',
      MA: '212', MC: '377', MD: '373', ME: '382', MF: '590', MG: '261', MH: '692', MK: '389', ML: '223', MM: '95', MN: '976', MO: '853', MP: '1', MQ: '596', MR: '222', MS: '1', MT: '356', MU: '230', MV: '960', MW: '265', MX: '52', MY: '60', MZ: '258',
      NA: '264', NC: '687', NE: '227', NF: '672', NG: '234', NI: '505', NL: '31', NO: '47', NP: '977', NR: '674', NU: '683', NZ: '64', OM: '968', PA: '507', PE: '51', PF: '689', PG: '675', PH: '63', PK: '92', PL: '48', PM: '508', PR: '1', PS: '970', PT: '351', PW: '680', PY: '595',
      QA: '974', RE: '262', RO: '40', RS: '381', RU: '7', RW: '250', SA: '966', SB: '677', SC: '248', SD: '249', SE: '46', SG: '65', SH: '290', SI: '386', SJ: '47', SK: '421', SL: '232', SM: '378', SN: '221', SO: '252', SR: '597', SS: '211', ST: '239', SV: '503', SX: '1', SY: '963', SZ: '268',
      TA: '290', TC: '1', TD: '235', TG: '228', TH: '66', TJ: '992', TK: '690', TL: '670', TM: '993', TN: '216', TO: '676', TR: '90', TT: '1', TV: '688', TW: '886', TZ: '255', UA: '380', UG: '256', US: '1', UY: '598', UZ: '998',
      VA: '39', VC: '1', VE: '58', VG: '1', VI: '1', VN: '84', VU: '678', WF: '681', WS: '685', XK: '383', YE: '967', YT: '262', ZA: '27', ZM: '260', ZW: '263',
    };
    if (typeof Intl.DisplayNames === 'function') {
      const canonicalRegionNames = new Intl.DisplayNames(['en-US'], { type: 'region' });
      Object.keys(callingCodeByCountry).forEach((code) => {
        const name = canonicalRegionNames.of(code);
        if (name && name !== code) countryCodeByName.set(normalizeCountryName(name), code);
      });
    }
    const callingCodeByName = new Map();
    countryCodeByName.forEach((isoCode, normalizedName) => {
      const dialCode = callingCodeByCountry[isoCode];
      if (dialCode) callingCodeByName.set(normalizedName, dialCode);
    });

    const addDays = (date, days) => {
      const next = new Date(date);
      next.setDate(next.getDate() + days);
      return next;
    };

    const fullDate = (date) => new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);

    const estimatedDeliveryRange = (currentDate, minimumDays, maximumDays) => {
      const firstDate = addDays(currentDate, minimumDays);
      const lastDate = addDays(currentDate, maximumDays);
      const month = new Intl.DateTimeFormat('en-US', { month: 'short' });
      const monthDay = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
      let range;
      if (firstDate.getFullYear() !== lastDate.getFullYear()) {
        range = `${fullDate(firstDate)}–${fullDate(lastDate)}`;
      } else if (firstDate.getMonth() !== lastDate.getMonth()) {
        range = `${monthDay.format(firstDate)}–${monthDay.format(lastDate)}, ${lastDate.getFullYear()}`;
      } else if (firstDate.getDate() !== lastDate.getDate()) {
        range = `${month.format(firstDate)} ${firstDate.getDate()}–${lastDate.getDate()}, ${lastDate.getFullYear()}`;
      } else {
        range = fullDate(firstDate);
      }
      return range;
    };

    // Mirrors the current Shopify shipping-profile order-amount tiers. Verified 2026-09-04.
    // Standard is free in normal zones; Europe + UK and Global multiply the base USD Express rate.
    const expressRateTables = {
      soft: [
        [20000, 6400], [40000, 12800], [60000, 19200], [80000, 25600],
        [100000, 32000], [120000, 38400], [140000, 44800], [160000, 51200],
        [180000, 57600], [200000, 64000], [220000, 70400], [240000, 76800],
        [260000, 83200], [300000, 89600], [400000, 96000], [500000, 102400],
        [600000, 108800], [Number.POSITIVE_INFINITY, 500000],
      ],
      hard: [
        [20000, 14000], [40099, 28000], [60100, 42000], [80000, 52000],
        [100000, 70000], [120000, 84000], [140000, 98000], [160000, 112000],
        [200000, 150000], [Number.POSITIVE_INFINITY, 250000],
      ],
    };
    const europeAndUkCountryCodes = new Set([
      'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE',
      'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'GB',
    ]);
    const specialUsRegions = new Set([
      'ak', 'alaska', 'as', 'american samoa', 'fm', 'federated states of micronesia',
      'gu', 'guam', 'hi', 'hawaii', 'mh', 'marshall islands', 'mp', 'northern mariana islands',
      'pw', 'palau', 'pr', 'puerto rico', 'vi', 'us virgin islands', 'u s virgin islands',
      'aa', 'armed forces americas', 'ae', 'armed forces europe', 'ap', 'armed forces pacific',
    ]);
    const normalizeRegionName = (value) => String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/gi, ' ')
      .trim()
      .toLocaleLowerCase('en-US');
    const deliveryZoneFor = (countryCode, region) => {
      const code = String(countryCode || '').trim().toUpperCase();
      if (code === 'US' && specialUsRegions.has(normalizeRegionName(region))) return 'us-special';
      if (code === 'US' || code === 'CA' || code === 'MX') return 'north-america';
      if (europeAndUkCountryCodes.has(code)) return 'europe-uk';
      return 'global';
    };
    const expressRateCentsFor = (group, orderAmountCents, zone) => {
      const table = expressRateTables[group] || expressRateTables.hard;
      const amount = Math.max(0, Number(orderAmountCents) || 0);
      if (!amount || zone === 'us-special') return null;
      const baseRate = table.find(([maximum]) => amount <= maximum)?.[1];
      if (!baseRate) return null;
      const multiplier = zone === 'europe-uk' ? (group === 'soft' ? 1.1 : 1.3) : zone === 'global' ? (group === 'soft' ? 1.15 : 1.4) : 1;
      return Math.round(baseRate * multiplier);
    };

    estimators.forEach((estimator) => {
      estimator.setAttribute('data-11p-delivery-ready', 'true');
      const addressToggle = estimator.querySelector('[data-11p-delivery-address-toggle]');
      const addressEditor = estimator.querySelector('[data-11p-delivery-address-editor]');
      const addressApply = estimator.querySelector('[data-11p-delivery-address-apply]');
      const countryInput = estimator.querySelector('[data-11p-delivery-country]');
      const regionInput = estimator.querySelector('[data-11p-delivery-region]');
      const destinationOutput = estimator.querySelector('[data-11p-delivery-destination]');
      const expressRangeOutput = estimator.querySelector('[data-11p-delivery-express-range]');
      const standardRangeOutput = estimator.querySelector('[data-11p-delivery-standard-range]');
      const expressPriceOutput = estimator.querySelector('[data-11p-delivery-express-price]');
      const standardPriceOutput = estimator.querySelector('[data-11p-delivery-standard-price]');
      const statusOutput = estimator.querySelector('[data-11p-delivery-status]');
      if (!addressToggle || !addressEditor || !addressApply || !countryInput || !regionInput || !destinationOutput) return;

      const geoEnabled = estimator.getAttribute('data-11p-delivery-geo-enabled') === 'true';
      const standardIncluded = estimator.getAttribute('data-11p-delivery-standard-included') === 'true';
      const transactionType = estimator.getAttribute('data-11p-delivery-transaction-type') || '';
      const rateGroup = estimator.getAttribute('data-11p-delivery-rate-group') || 'hard';
      const shippingCurrency = estimator.getAttribute('data-11p-delivery-currency') || 'USD';
      const readDeliveryDays = (name, fallback) => {
        const value = Number(estimator.getAttribute(name));
        return Number.isFinite(value) && value > 0 ? Math.floor(value) : fallback;
      };
      const standardDeliveryMinDays = readDeliveryDays('data-11p-delivery-standard-min-days', rateGroup === 'soft' ? 20 : 40);
      const standardDeliveryMaxDays = Math.max(
        standardDeliveryMinDays,
        readDeliveryDays('data-11p-delivery-standard-max-days', rateGroup === 'soft' ? 25 : 50),
      );
      const expressDeliveryMinDays = readDeliveryDays('data-11p-delivery-express-min-days', rateGroup === 'soft' ? 14 : 20);
      const expressDeliveryMaxDays = Math.max(
        expressDeliveryMinDays,
        readDeliveryDays('data-11p-delivery-express-max-days', rateGroup === 'soft' ? 18 : 25),
      );
      const baseOrderAmountCents = Math.max(0, Number(estimator.getAttribute('data-11p-delivery-order-amount-cents')) || 0);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const configuredCountry = estimator.getAttribute('data-11p-delivery-default-country');
      const configuredCountryName = estimator.getAttribute('data-11p-delivery-default-country-name');
      const countryOptions = [...countryInput.options]
        .filter((option) => option.value && option.value !== '---')
        .map((option) => {
          const name = option.textContent.trim();
          const rawOptionCode = String(option.value || '').trim().toUpperCase();
          const code = (callingCodeByCountry[rawOptionCode] ? rawOptionCode : '')
            || countryCodeByName.get(normalizeCountryName(name))
            || option.dataset.countryCode
            || '';
          const dialCode = callingCodeByCountry[code]
            || callingCodeByName.get(normalizeCountryName(name))
            || '';
          option.dataset.countryName = name;
          option.dataset.countryCode = code;
          option.dataset.dialCode = dialCode;
          option.dataset.valueLabel = name;
          option.dataset.searchValue = `${name} ${code} ${dialCode} +${dialCode}`.trim();
          option.value = code || name;
          return option;
        })
        .sort((first, second) => first.dataset.countryName.localeCompare(second.dataset.countryName, 'en-US'));
      countryInput.replaceChildren(...countryOptions);
      const configuredOption = countryOptions.find((option) => option.value === configuredCountry)
        || countryOptions.find((option) => option.dataset.countryName === configuredCountryName)
        || countryOptions[0];
      if (configuredOption) configuredOption.selected = true;
      initAnchoredSelect(countryInput);
      regionInput.value = estimator.getAttribute('data-11p-delivery-default-region') || '';

      let appliedCountry = countryInput.value;
      let appliedRegion = regionInput.value.trim();
      let appliedOrderAmountCents = baseOrderAmountCents;
      let destinationEdited = false;

      const purchaseCard = estimator.closest('.elevenp-purchase-card');
      const quantitySelector = estimator.getAttribute('data-11p-delivery-quantity-selector');
      let quantityControl = null;
      if (quantitySelector && purchaseCard) {
        try {
          quantityControl = purchaseCard.querySelector(quantitySelector);
        } catch (_) {
          quantityControl = null;
        }
      }
      const syncOrderAmountFromQuantity = () => {
        if (transactionType !== 'standard_product') return;
        const quantity = Math.max(1, Number(quantityControl?.value) || 1);
        appliedOrderAmountCents = baseOrderAmountCents * quantity;
      };
      const formatShippingMoney = (amountCents) => new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: shippingCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amountCents / 100);

      const getCountryName = () => countryInput.options[countryInput.selectedIndex]?.dataset.countryName
        || countryInput.options[countryInput.selectedIndex]?.textContent?.trim()
        || appliedCountry;

      const updateEstimate = ({ announce = false } = {}) => {
        const destination = appliedRegion ? `${appliedRegion}, ${getCountryName()}` : getCountryName();
        const zone = deliveryZoneFor(appliedCountry, appliedRegion);
        const expressRateCents = expressRateCentsFor(rateGroup, appliedOrderAmountCents, zone);
        const standardIsFree = standardIncluded && zone !== 'us-special';
        const standardRange = estimatedDeliveryRange(today, standardDeliveryMinDays, standardDeliveryMaxDays);
        const expressRange = estimatedDeliveryRange(today, expressDeliveryMinDays, expressDeliveryMaxDays);
        destinationOutput.textContent = destination;
        if (standardRangeOutput) standardRangeOutput.textContent = standardIsFree ? standardRange : 'Calculated at checkout';
        if (standardPriceOutput) standardPriceOutput.textContent = standardIsFree ? 'Free' : 'Calculated at checkout';
        if (expressRangeOutput) expressRangeOutput.textContent = expressRateCents ? expressRange : 'Calculated at checkout';
        if (expressPriceOutput) expressPriceOutput.textContent = expressRateCents ? formatShippingMoney(expressRateCents) : 'Calculated at checkout';
        if (announce && statusOutput) {
          const expressMessage = expressRateCents ? ` Estimated Express shipping is ${formatShippingMoney(expressRateCents)}.` : '';
          statusOutput.textContent = `Destination updated to ${destination}.${expressMessage} Final eligibility and rates are confirmed at checkout.`;
        }
      };

      syncOrderAmountFromQuantity();
      quantityControl?.addEventListener('input', () => {
        syncOrderAmountFromQuantity();
        updateEstimate();
      });
      quantityControl?.addEventListener('change', () => {
        syncOrderAmountFromQuantity();
        updateEstimate();
      });
      purchaseCard?.addEventListener('11p:order-amount-change', (event) => {
        const amountCents = Number(event.detail?.amountCents);
        if (!Number.isFinite(amountCents) || amountCents <= 0) return;
        appliedOrderAmountCents = amountCents;
        updateEstimate();
      });

      const closeAddressEditor = ({ restore = true } = {}) => {
        addressEditor.hidden = true;
        addressToggle.setAttribute('aria-expanded', 'false');
        if (restore) {
          countryInput.value = appliedCountry;
          regionInput.value = appliedRegion;
          countryInput._elevenpRefreshSelect?.();
        }
      };

      addressToggle.addEventListener('click', () => {
        const shouldOpen = addressEditor.hidden;
        if (!shouldOpen) {
          closeAddressEditor();
          return;
        }
        countryInput.value = appliedCountry;
        regionInput.value = appliedRegion;
        countryInput._elevenpRefreshSelect?.();
        addressEditor.hidden = false;
        addressToggle.setAttribute('aria-expanded', 'true');
        countryInput.closest('[data-11p-select]')?.querySelector('.elevenp-select-trigger')?.focus();
      });

      addressApply.addEventListener('click', () => {
        destinationEdited = true;
        appliedCountry = countryInput.value;
        appliedRegion = regionInput.value.trim();
        updateEstimate({ announce: true });
        closeAddressEditor({ restore: false });
        addressToggle.focus();
      });

      countryInput.addEventListener('change', () => {
        destinationEdited = true;
      });
      regionInput.addEventListener('input', () => {
        destinationEdited = true;
      });

      estimator.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        if (!addressEditor.hidden) {
          closeAddressEditor();
          addressToggle.focus();
        }
      });

      updateEstimate();

      if (geoEnabled) {
        getDeliveryGeo().then((detected) => {
          if (!detected || destinationEdited) return;
          const detectedOption = countryOptions.find((option) => option.dataset.countryCode === detected.countryCode);
          if (!detectedOption) return;
          const detectedRegion = detected.region || detected.city;
          countryInput.value = detectedOption.value;
          regionInput.value = detectedRegion;
          appliedCountry = detectedOption.value;
          appliedRegion = detectedRegion;
          countryInput._elevenpRefreshSelect?.();
          updateEstimate();
        });
      }
    });
  };

  const initRelated = (scope = document) => {
    scope.querySelectorAll('[data-11p-related]:not([data-11p-ready])').forEach((section) => {
      section.setAttribute('data-11p-ready', 'true');
      const viewport = section.querySelector('[data-11p-related-viewport]');
      const previous = section.querySelector('[data-11p-related-previous]');
      const next = section.querySelector('[data-11p-related-next]');
      if (!viewport || !previous || !next) return;

      const update = () => {
        previous.disabled = viewport.scrollLeft <= 2;
        next.disabled = viewport.scrollLeft + viewport.clientWidth >= viewport.scrollWidth - 2;
      };
      const move = (direction) => viewport.scrollBy({ left: direction * viewport.clientWidth * .9, behavior: 'smooth' });
      previous.addEventListener('click', () => move(-1));
      next.addEventListener('click', () => move(1));
      viewport.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  };

  let spVariantSubscription;
  const initSpTiered = (scope = document) => {
    scope.querySelectorAll('[data-11p-sp-tiered]:not([data-11p-tiered-ready])').forEach((root) => {
      root.setAttribute('data-11p-tiered-ready', 'true');
      const source = root.querySelector('[data-11p-sp-variants]');
      const nativeBatch = root.hasAttribute('data-11p-sp-native-batch');
      let quantityTierMatrix = {};
      if (nativeBatch) {
        try {
          quantityTierMatrix = JSON.parse(root.querySelector('[data-11p-quantity-tier-matrix]')?.textContent || '{}');
        } catch (_) {
          quantityTierMatrix = {};
        }
      }
      const quantitySelect = root.querySelector('[data-11p-sp-quantity-select]');
      const quantityProperty = root.querySelector('[data-11p-sp-quantity-property]');
      const totalQuantity = root.querySelector('[data-11p-sp-total-quantity]');
      const total = root.querySelector('[data-11p-sp-total]');
      const productForm = root.querySelector('product-form');
      const form = productForm?.querySelector('form');
      const variantIdInput = form?.querySelector('input[name="id"]');
      const submitButton = form?.querySelector('button[type="submit"]');
      const submitLabel = submitButton?.querySelector('span');
      const selectedVariantSource = root.querySelector('[data-selected-variant]');
      const optionFields = [...root.querySelectorAll('[data-11p-sp-option-field]')]
        .map((field) => ({
          field,
          select: field.querySelector('[data-11p-sp-option]'),
          index: Number(field.dataset.optionIndex),
        }))
        .filter(({ select, index }) => select && Number.isInteger(index));
      const rows = [...root.querySelectorAll('[data-11p-sp-tier-row]')];
      const addonSource = root.querySelector('[data-11p-sp-addon-matrix]');
      let addonMatrix;
      try {
        addonMatrix = addonSource ? JSON.parse(addonSource.textContent) : null;
        if (addonMatrix && (addonMatrix.version !== 1
          || String(addonMatrix.product_id) !== addonSource.dataset.productId
          || addonMatrix.currency !== root.dataset.currency
          || !Array.isArray(addonMatrix.values) || !addonMatrix.deltas)) addonMatrix = null;
      } catch (_) { addonMatrix = null; }
      let addonInput;
      let lastAddonCents;
      let syncingAddon = false;
      let addonLoadFailed = false;
      const appField = (label) => [...root.querySelectorAll('ymq-option .ymq-options-box[ymq-option-label]')]
        .find((field) => field.getAttribute('ymq-option-label')?.replace(/^_/, '') === label);
      const addonSelect = () => addonMatrix ? appField(addonMatrix.field)?.querySelector('select') : null;
      const addonValue = () => addonSelect()?.selectedOptions[0]?.textContent.trim();
      const placeAddonOptions = () => {
        if (!addonMatrix || !form) return;
        const options = root.querySelector('[data-11p-sp-addon-options]');
        const table = root.querySelector('[data-11p-sp-tier-table]');
        const summary = root.querySelector('[data-11p-sp-total-summary]');
        const host = appField(addonMatrix.field)?.closest('ymq-option');
        if (!options || !table || !summary || !host) return;
        // Keep the app host and all its generated inputs inside their original cart form.
        if (options.parentElement !== form) form.prepend(options, table, summary);
        if (host.parentElement !== options) options.append(host);
      };
      const addonCents = (variant) => {
        if (!addonSource) return 0;
        if (Number(variant?.price) !== addonMatrix?.base_cents?.[String(variant?.id)]) return null;
        const value = addonMatrix?.deltas?.[String(variant?.id)]?.[addonValue()];
        return Number.isSafeInteger(value) && value >= 0 ? value : null;
      };
      const quotedPrice = (variant) => {
        const delta = addonCents(variant);
        return delta === null ? null : Number(variant?.price) + delta;
      };
      const syncAddon = (cents) => {
        if (!addonSource) return true;
        const field = appField('Additional');
        const input = field?.querySelector('input[type="text"], input');
        if (!addonMatrix || !input || !Number.isSafeInteger(cents) || cents < 0
          || !window.ymqOption?._setAttr) return false;
        if (!field.hidden) field.hidden = true;
        field.style.setProperty('display', 'none', 'important');
        field.setAttribute('ymq-option-label', '_Additional');
        field.setAttribute('aria-hidden', 'true');
        input.tabIndex = -1;
        if (addonInput === input && lastAddonCents === cents) return true;
        syncingAddon = true;
        try {
          const amount = cents / 100;
          const addonTotal = root.querySelector('#ymq_addon_total')
            || form?.querySelector('#ymq_addon_total') || document.getElementById('ymq_addon_total');
          input.value = cents ? 'Configured options' : '';
          input.setAttribute('add-price', amount.toFixed(2));
          window.ymqOption._setAttr(input, 'price', amount);
          window.ymqOption._triggerInput?.(input);
          if (addonTotal) {
            addonTotal.setAttribute('add-price', amount.toFixed(2));
            window.ymqOption._setAttr(addonTotal, 'price', amount);
            window.ymqOption._triggerInput?.(addonTotal);
          }
          window.ymqOption.buildFormData?.(input.closest('ymq-option'));
          addonInput = input;
          lastAddonCents = cents;
        } finally { syncingAddon = false; }
        return true;
      };
      const quantityIndex = Number(root.dataset.quantityOptionIndex);
      if ((!source && !nativeBatch) || !quantitySelect || !rows.length || !Number.isInteger(quantityIndex)) return;

      let variants = [];
      if (!nativeBatch) {
        try {
          variants = JSON.parse(source.textContent || '[]');
        } catch (_) {
          return;
        }
        if (!Array.isArray(variants) || !variants.length) return;
      }
      let nativePricingReady = !nativeBatch;
      let nativePricingError = false;
      let nativeRequestSequence = 0;
      let nativePriceRequest;

      const currency = root.dataset.currency || 'USD';
      const locale = root.dataset.locale || 'en-US';
      const validVariant = (variant) => variant?.available && Number(variant.price) > 0
        && (!nativeBatch || nativePricingReady);
      const currentOptionValues = () => {
        const values = [];
        optionFields.forEach(({ select, index }) => { values[index] = select.value; });
        values[quantityIndex] = quantitySelect.value;
        return values;
      };
      const matches = (variant, values, exceptIndex = -1) => values.every((value, index) => (
        index === exceptIndex || !value || variant.options?.[index] === value
      ));

      const refreshOptionAvailability = () => {
        // A batch response contains only the current configuration's variants.
        // It cannot determine whether another option value is globally valid.
        if (nativeBatch) return;
        const values = currentOptionValues();
        optionFields.forEach(({ select, index }, fieldPosition) => {
          const upstreamIndexes = optionFields.slice(0, fieldPosition).map((field) => field.index);
          [...select.options].forEach((option) => {
            if (!option.value) return;
            option.disabled = !variants.some((variant) => validVariant(variant)
              && variant.options?.[index] === option.value
              && upstreamIndexes.every((upstreamIndex) => (
                !values[upstreamIndex] || variant.options?.[upstreamIndex] === values[upstreamIndex]
              )));
          });
          const selected = select.options[select.selectedIndex];
          if (selected?.disabled) {
            const fallback = [...select.options].find((option) => option.value && !option.disabled);
            if (fallback) select.value = fallback.value;
          }
          select.closest('[data-11p-sp-option-field]')?.classList.toggle(
            'is-unavailable',
            ![...select.options].some((option) => option.value && !option.disabled),
          );
          select._elevenpRefreshSelect?.();
        });
      };

      const refreshTiers = () => {
        placeAddonOptions();
        const values = currentOptionValues();
        const colorSelect = addonSelect();
        if (colorSelect) {
          [...colorSelect.options].forEach((option) => {
            const name = option.textContent.trim();
            if (!addonMatrix.values.includes(name)) return;
            const allowed = variants.some((variant) => matches(variant, values, quantityIndex)
              && Number.isSafeInteger(addonMatrix.deltas[String(variant.id)]?.[name]));
            if (option.disabled === allowed) option.disabled = !allowed;
            if (option.hidden === allowed) option.hidden = !allowed;
          });
          if (colorSelect.selectedOptions[0]?.disabled) {
            const fallback = [...colorSelect.options].find((option) => !option.disabled
              && option.textContent.trim() === addonMatrix.default);
            if (fallback) {
              colorSelect.value = fallback.value;
              colorSelect.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
          colorSelect._elevenpRefreshSelect?.();
        }
        const tierData = rows.map((row) => {
          const quantityValue = row.dataset.tierValue;
          const requested = [...values];
          requested[quantityIndex] = quantityValue;
          const variant = variants.find((candidate) => matches(candidate, requested));
          return { row, variant, price: quotedPrice(variant), quantity: parseQuantity(quantityValue) };
        });
        const firstAvailable = tierData.find(({ variant, price, quantity }) => validVariant(variant) && price > 0 && quantity > 0);
        const baseUnit = firstAvailable ? firstAvailable.price / firstAvailable.quantity : 0;

        tierData.forEach(({ row, variant, price, quantity }) => {
          if (nativeBatch) row._elevenpVariant = variant || null;
          const available = validVariant(variant) && price > 0 && quantity > 0;
          const input = row.querySelector('input');
          row.classList.toggle('is-unavailable', !available);
          row.hidden = !variant;
          if (input) input.disabled = !available;
          if (input && addonSource) input.setAttribute('aria-label', `${quantity.toLocaleString(locale)} units — ${available ? formatCurrency(price, currency, locale) : 'Unavailable'}`);
          row.querySelector('[data-11p-sp-tier-total]').textContent = available
            ? formatCurrency(price, currency, locale)
            : '—';
          row.querySelector('[data-11p-sp-tier-unit]').textContent = available
            ? `${formatUnitPrice(price, quantity, currency, locale)}/unit`
            : '—';
          const unitPrice = available ? price / quantity : 0;
          const saving = available && baseUnit > 0 ? Math.max(0, Math.round((1 - unitPrice / baseUnit) * 100)) : 0;
          row.querySelector('[data-11p-sp-tier-save]').textContent = available && saving > 0 ? `${saving}%` : '—';
        });

        let selected = tierData.find(({ row }) => row.dataset.tierValue === quantitySelect.value && !row.classList.contains('is-unavailable'));
        if (!selected) selected = firstAvailable;
        if (selected) quantitySelect.value = selected.row.dataset.tierValue;
        rows.forEach((row) => {
          const checked = row === selected?.row;
          row.classList.toggle('is-selected', checked);
          const input = row.querySelector('input');
          if (input) input.checked = checked;
        });
        if (quantityProperty) quantityProperty.value = quantitySelect.value;
        if (!selected?.variant) {
          if (nativeBatch || addonSource) {
            if (variantIdInput) variantIdInput.disabled = true;
            if (submitButton) submitButton.disabled = true;
            if (submitLabel) submitLabel.textContent = addonSource ? (addonLoadFailed ? 'Options unavailable' : 'Loading options…') : nativePricingError ? 'Price unavailable'
              : nativePricingReady ? 'Unavailable' : 'Loading prices…';
            productForm?.setAttribute('data-11p-sp-valid-price', 'false');
            if (totalQuantity) totalQuantity.textContent = '—';
            if (total) total.textContent = '—';
          }
          return;
        }

        const selectedVariant = selected.variant;
        if (nativeBatch || addonSource) {
          if (variantIdInput) variantIdInput.disabled = false;
          if (submitButton) submitButton.disabled = false;
          if (submitLabel) submitLabel.textContent = 'Add to Cart';
        }
        if (variantIdInput && String(variantIdInput.value) !== String(selectedVariant.id)) {
          variantIdInput.value = selectedVariant.id;
          variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (selectedVariantSource) selectedVariantSource.textContent = JSON.stringify(selectedVariant);
        const addonReady = syncAddon(addonCents(selectedVariant));
        if (addonSource && submitButton) submitButton.disabled = !addonReady;
        if (addonSource && submitLabel) submitLabel.textContent = addonReady ? 'Add to Cart' : addonLoadFailed ? 'Options unavailable' : 'Loading options…';
        if (totalQuantity) totalQuantity.textContent = `${selected.quantity.toLocaleString(locale)} units`;
        if (total) total.textContent = formatCurrency(selected.price, currency, locale);
        productForm?.setAttribute('data-11p-sp-valid-price', String(validVariant(selectedVariant) && addonReady));
        root.dispatchEvent(new CustomEvent('11p:order-amount-change', {
          bubbles: true,
          detail: { amountCents: selected.price || 0 },
        }));
      };

      const readNativePayload = (payload, requestedOptions) => {
        const quantities = rows.map((row) => row.dataset.tierValue);
        const allowed = allowedQuantityTiers(quantityTierMatrix, requestedOptions, quantityIndex, quantities);
        let expectedCounts = [];
        try { expectedCounts = JSON.parse(root.dataset.expectedOptionCounts || '[]'); } catch (_) { expectedCounts = []; }
        const expectedCount = Number(root.dataset.expectedVariantCount);
        if (!allowed || !Number.isInteger(expectedCount) || expectedCount < 1
          || expectedCount !== Number(quantityTierMatrix.expected_variant_count)
          || !Array.isArray(expectedCounts) || expectedCounts.length !== requestedOptions.length
          || expectedCount > expectedCounts.reduce((count, value) => count * Number(value || 0), 1)
          || String(payload?.productId) !== root.dataset.productId
          || payload.variantCount !== expectedCount
          || !Array.isArray(payload.optionCounts)
          || !expectedCounts.every((count, index) => payload.optionCounts[index] === count)
          || !Array.isArray(payload.options)
          || !requestedOptions.every((value, index) => payload.options[index] === value)
          || !Array.isArray(payload.tiers) || payload.tiers.length !== rows.length) {
          throw new Error('The returned quantity matrix does not match this product.');
        }
        const seen = new Set();
        const loaded = [];
        payload.tiers.forEach((tier) => {
          const row = rows.find((candidate) => candidate.dataset.tierValue === tier.quantityValue);
          if (!row || seen.has(tier.quantityValue)
            || String(tier.optionValueId) !== row.dataset.optionValueId) {
            throw new Error('The returned quantity rows are incomplete.');
          }
          seen.add(tier.quantityValue);
          if (!allowed.has(tier.quantityValue)) {
            if (tier.variant !== null) throw new Error('An unquoted quantity has a variant.');
            return;
          }
          const variant = tier.variant;
          if (!variant?.id || !Number.isFinite(variant.price) || variant.price <= 0
            || typeof variant.available !== 'boolean' || !Array.isArray(variant.options)
            || !requestedOptions.every((value, index) => (
              index === quantityIndex ? variant.options[index] === tier.quantityValue : variant.options[index] === value
            ))) {
            throw new Error('A quoted quantity has no matching priced variant.');
          }
          loaded.push(variant);
        });
        if (seen.size !== rows.length || loaded.length !== allowed.size) {
          throw new Error('The quoted quantity list is incomplete.');
        }
        return loaded;
      };

      const loadNativeTiers = async () => {
        const sequence = ++nativeRequestSequence;
        nativePriceRequest?.abort();
        nativePriceRequest = new AbortController();
        const requestedOptions = currentOptionValues();
        const quantities = rows.map((row) => row.dataset.tierValue);
        const allowed = allowedQuantityTiers(quantityTierMatrix, requestedOptions, quantityIndex, quantities);
        const preferred = allowed?.has(quantitySelect.value) ? quantitySelect.value
          : rows.find((row) => allowed?.has(row.dataset.tierValue))?.dataset.tierValue;
        requestedOptions[quantityIndex] = preferred;
        nativePricingReady = false;
        nativePricingError = false;
        variants = [];
        refreshTiers();
        try {
          const optionValueIds = [];
          optionFields.forEach(({ select, index }) => {
            optionValueIds[index] = select.options[select.selectedIndex]?.dataset.optionValueId;
          });
          const quantityOption = [...quantitySelect.options].find((option) => option.value === preferred);
          optionValueIds[quantityIndex] = quantityOption?.dataset.optionValueId;
          if (!preferred || optionValueIds.length !== requestedOptions.length
            || optionValueIds.some((value) => !value) || !requestedOptions.every(Boolean)) {
            throw new Error('Choose a quoted configuration.');
          }
          const url = new URL(root.dataset.productUrl, window.location.origin);
          const previewThemeId = new URL(window.location.href).searchParams.get('preview_theme_id');
          if (previewThemeId) url.searchParams.set('preview_theme_id', previewThemeId);
          url.searchParams.delete('variant');
          url.searchParams.set('section_id', root.dataset.sectionId);
          url.searchParams.set('option_values', optionValueIds.join(','));
          const response = await fetch(url, { signal: nativePriceRequest.signal, credentials: 'same-origin' });
          if (!response.ok) throw new Error('Prices could not be loaded.');
          const html = await response.text();
          if (sequence !== nativeRequestSequence || !root.isConnected) return;
          const fragment = new DOMParser().parseFromString(html, 'text/html');
          const payloadNode = fragment.querySelector('[data-11p-sp-native-tiers]');
          const payload = JSON.parse(payloadNode?.textContent || 'null');
          variants = readNativePayload(payload, requestedOptions);
          nativePricingReady = true;
          nativePricingError = false;
          refreshTiers();
        } catch (error) {
          if (error.name === 'AbortError' || sequence !== nativeRequestSequence) return;
          variants = [];
          nativePricingReady = false;
          nativePricingError = true;
          refreshTiers();
        }
      };

      const refresh = () => {
        refreshOptionAvailability();
        refreshTiers();
      };
      optionFields.forEach(({ select }) => select.addEventListener('change', nativeBatch ? loadNativeTiers : refresh));
      quantitySelect.addEventListener('change', refresh);
      if (addonSource) {
        window.addEventListener('pageshow', () => {
          window.requestAnimationFrame(() => window.setTimeout(refresh, 0));
        });
        root.addEventListener('change', (event) => {
          if (!syncingAddon && event.target === addonSelect()) refreshTiers();
        });
        form?.addEventListener('submit', (event) => {
          const selected = variants.find((variant) => matches(variant, currentOptionValues()));
          if (!selected || !syncAddon(addonCents(selected))) {
            event.preventDefault();
            event.stopImmediatePropagation();
          }
        }, true);
        let attempts = 0;
        const connect = window.setInterval(() => {
          if (!root.isConnected) { window.clearInterval(connect); return; }
          if (++attempts > 80) {
            addonLoadFailed = true;
            refreshTiers();
            window.clearInterval(connect);
            return;
          }
          refreshTiers();
          if (addonSelect() && addonInput && window.ymqOption?._setAttr) window.clearInterval(connect);
        }, 250);
      }
      rows.forEach((row) => {
        const input = row.querySelector('input');
        if (!input || input.dataset.elevenpSpTierReady === 'true') return;
        input.dataset.elevenpSpTierReady = 'true';
        input.addEventListener('change', () => {
          if (row.classList.contains('is-unavailable')) return;
          quantitySelect.value = row.dataset.tierValue;
          if (quantityProperty) quantityProperty.value = quantitySelect.value;
          refresh();
          quantitySelect.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
      if (nativeBatch) {
        form?.addEventListener('submit', (event) => {
          const selected = rows.find((row) => row.classList.contains('is-selected'));
          if (!nativePricingReady || !selected?._elevenpVariant?.available || variantIdInput?.disabled) {
            event.preventDefault();
            event.stopImmediatePropagation();
          }
        }, true);
        try {
          const payload = JSON.parse(root.querySelector('[data-11p-sp-native-tiers]')?.textContent || 'null');
          variants = readNativePayload(payload, currentOptionValues());
          nativePricingReady = true;
          refreshTiers();
        } catch (_) {
          loadNativeTiers();
        }
      } else {
        refresh();
      }
    });
  };

  // Keep YMQ as the value/validation owner; reuse the shared SP dropdown UI.
  const initSpAppSelects = (scope = document) => {
    const selector = '.elevenp-sp-card ymq-option select.ymq-dropdown';
    const selects = [...scope.querySelectorAll(selector)];
    if (scope.matches?.(selector)) selects.push(scope);
    selects.forEach((select) => {
      if (select.dataset.elevenpSelectReady === 'true' || select.multiple) return;
      const wrapper = select.closest('.ymq-option-select-wrapper');
      if (!wrapper) return;
      wrapper.classList.add('elevenp-select');
      wrapper.setAttribute('data-11p-select', '');
      wrapper.dataset.elevenpAppSelect = 'true';
      const caret = select.closest('.elevenp-sp-card')?.querySelector('[data-11p-select]:not([data-elevenp-app-select]) .svg-wrapper');
      if (caret) {
        wrapper.append(caret.cloneNode(true));
        wrapper.classList.add('has-theme-caret');
      }
      const label = select.closest('.ymq-options-box')?.getAttribute('ymq-option-label');
      if (label && !select.hasAttribute('aria-label')) select.setAttribute('aria-label', label);
      initAnchoredSelect(select);
    });

    const imageFields = [...scope.querySelectorAll('.elevenp-sp-card ymq-option .ymq-options-box[ymq-option-label]')];
    if (scope.matches?.('.elevenp-sp-card ymq-option .ymq-options-box[ymq-option-label]')) imageFields.push(scope);
    imageFields.forEach((field) => {
      if (field.dataset.elevenpImageSelectReady === 'true') return;
      const radios = [...field.querySelectorAll('input[type="radio"]')];
      if (radios.length < 2) return;
      const readChoice = (radio) => {
        const label = radio.closest('label')
          || (radio.id ? [...field.querySelectorAll('label[for]')].find((candidate) => candidate.htmlFor === radio.id) : null);
        const image = label?.querySelector('img');
        const imageUrl = image?.currentSrc || image?.src || image?.dataset.src || image?.getAttribute('data-src') || '';
        const value = String(radio.value || image?.alt || label?.textContent || '').trim();
        return { label, imageUrl, value };
      };
      const choices = radios.map((radio) => ({ radio, ...readChoice(radio) }));
      if (choices.some((choice) => !choice.value || !choice.imageUrl)) return;

      // Hide only the source radio group visually. Keep YMQ's real inputs enabled
      // and in the form so its pricing, validation, and cart-property logic remain authoritative.
      const sourceGroup = (() => {
        let node = choices[0].radio;
        while (node?.parentElement && node.parentElement !== field) node = node.parentElement;
        return node?.parentElement === field ? node : null;
      })();
      if (!sourceGroup || choices.some(({ radio }) => !sourceGroup.contains(radio))) return;

      const label = field.getAttribute('ymq-option-label')?.trim() || 'Choose an option';
      const wrapper = document.createElement('span');
      wrapper.className = 'ymq-option-select-wrapper elevenp-select elevenp-sp-image-select';
      wrapper.setAttribute('data-11p-select', '');
      wrapper.dataset.elevenpAppSelect = 'true';
      wrapper.dataset.elevenpSelectImageSource = 'ymq-radio';
      const select = document.createElement('select');
      select.setAttribute('aria-label', label);
      select.setAttribute('data-11p-image-select-source', 'ymq-radio');
      select.required = field.getAttribute('ymq-option-validate')?.includes('required') || radios.some((radio) => radio.required);
      const selected = choices.find(({ radio }) => radio.checked);
      if (!selected) {
        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = 'Choose an option';
        placeholder.disabled = true;
        placeholder.selected = true;
        placeholder.className = 'ymq-option-select-placeholder';
        select.append(placeholder);
      }
      choices.forEach(({ radio, value, imageUrl, label: choiceLabel }) => {
        const option = document.createElement('option');
        option.value = radio.value;
        option.textContent = value;
        option.selected = radio.checked;
        option.disabled = radio.disabled || Boolean(choiceLabel?.hidden);
        option.dataset.valueLabel = value;
        option.dataset.imageUrl = imageUrl;
        option.dataset.imageSize = field.dataset.imageSize || 'large';
        select.append(option);
      });
      if (selected) select.value = selected.radio.value;
      const caret = document.createElement('span');
      caret.className = 'svg-wrapper';
      caret.setAttribute('aria-hidden', 'true');
      caret.innerHTML = '<svg viewBox="0 0 10 6" aria-hidden="true" focusable="false"><path d="m1 1 4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.25"/></svg>';
      wrapper.append(select, caret);
      sourceGroup.classList.add('elevenp-sp-image-radio-source');
      sourceGroup.setAttribute('aria-hidden', 'true');
      radios.forEach((radio) => {
        radio.tabIndex = -1;
        radio.addEventListener('invalid', (event) => {
          event.preventDefault();
          wrapper.querySelector('.elevenp-select-trigger')?.focus();
        });
      });
      sourceGroup.after(wrapper);

      const syncSelectFromRadio = (radio) => {
        if (!radio.checked) return;
        const match = [...select.options].find((option) => option.value === radio.value);
        if (match && select.value !== match.value) {
          select.value = match.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      };
      choices.forEach(({ radio }) => radio.addEventListener('change', () => syncSelectFromRadio(radio)));
      select.addEventListener('change', () => {
        const radio = choices.find((choice) => choice.radio.value === select.value)?.radio;
        if (radio && !radio.checked) radio.click();
      });
      const refreshSourceState = () => {
        choices.forEach(({ radio }) => {
          const option = [...select.options].find((candidate) => candidate.value === radio.value);
          if (option) option.disabled = radio.disabled;
        });
        select._elevenpRebuildSelect?.();
      };
      const sourceObserver = new MutationObserver(refreshSourceState);
      sourceObserver.observe(sourceGroup, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'hidden', 'checked'] });
      field.dataset.elevenpImageSelectReady = 'true';
      initAnchoredSelect(select);
      const selectedImageUrl = select.options[select.selectedIndex]?.dataset.imageUrl;
      if (selectedImageUrl) {
        wrapper.classList.add('elevenp-image-select-pending');
        const preload = new Image();
        const reveal = () => wrapper.classList.remove('elevenp-image-select-pending');
        preload.addEventListener('load', reveal, { once: true });
        preload.addEventListener('error', reveal, { once: true });
        preload.src = selectedImageUrl;
        if (preload.complete) reveal();
      }
    });

    const spCards = [...scope.querySelectorAll('.elevenp-sp-card:not([data-11p-sp-ymq-observer])')];
    if (scope.matches?.('.elevenp-sp-card:not([data-11p-sp-ymq-observer])')) spCards.push(scope);
    spCards.forEach((card) => {
      card.setAttribute('data-11p-sp-ymq-observer', 'true');
      let scheduled = false;
      const observer = new MutationObserver(() => {
        if (scheduled) return;
        scheduled = true;
        window.requestAnimationFrame(() => {
          scheduled = false;
          initSpAppSelects(card);
        });
      });
      observer.observe(card, { childList: true, subtree: true });
    });
  };

  const initSp = (scope = document) => {
    initSpAppSelects(scope);
    initSpTiered(scope);
    scope.querySelectorAll('.elevenp-sp-card [data-11p-select] select').forEach(initAnchoredSelect);

    scope.querySelectorAll('[data-11p-sp-mobile-cta]:not([data-11p-ready])').forEach((cta) => {
      cta.setAttribute('data-11p-ready', 'true');
      const purchaseCard = document.getElementById('sp-purchase');
      if (!purchaseCard || !('IntersectionObserver' in window)) return;
      const observer = new IntersectionObserver(([entry]) => {
        cta.classList.toggle('is-hidden', entry.isIntersecting);
      }, { threshold: .1 });
      observer.observe(purchaseCard);
    });

    if (spVariantSubscription || typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS !== 'object') return;
    spVariantSubscription = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      const currentRoot = document.getElementById(`MainProduct-${data.sectionId}`);
      const currentSpCard = currentRoot?.querySelector('[data-11p-sp-tiered]');
      currentSpCard?.removeAttribute('data-11p-tiered-ready');
      if (currentRoot) initSpAppSelects(currentRoot);
      initSpTiered(currentRoot);
      currentRoot?.querySelectorAll('.elevenp-sp-card [data-11p-select] select').forEach(initAnchoredSelect);
      const currentForm = currentRoot?.querySelector('.elevenp-sp-card product-form');
      if (!currentForm) return;
      const sourceRoot = data.html.getElementById(`MainProduct-${data.sectionId}`);
      const sourceForm = sourceRoot?.querySelector('.elevenp-sp-card product-form');
      const sourceButton = sourceRoot?.querySelector(`#ProductSubmitButton-${data.sectionId}`);
      const currentButton = currentForm.querySelector(`#ProductSubmitButton-${data.sectionId}`);
      if (!sourceForm || !sourceButton || !currentButton) return;

      let selectedVariant = null;
      try {
        selectedVariant = JSON.parse(sourceRoot.querySelector('[data-selected-variant]')?.textContent || 'null');
      } catch (_) {
        selectedVariant = null;
      }
      const validPrice = selectedVariant
        ? Number(selectedVariant.price) > 0
        : sourceForm.dataset.elevenpSpValidPrice === 'true';
      const canPurchase = selectedVariant
        ? selectedVariant.available !== false && validPrice
        : !sourceButton.disabled && validPrice;
      currentForm.dataset.elevenpSpValidPrice = String(validPrice);
      currentButton.disabled = !canPurchase;
      const currentLabel = currentButton.querySelector('span:first-child');
      const sourceLabel = sourceButton.querySelector('span:first-child');
      if (currentLabel && sourceLabel) currentLabel.textContent = sourceLabel.textContent;
    });
  };

  const initialize = (scope = document) => {
    initRfqForm(scope);
    initFaqGroups(scope);
    initProductZoom(scope);
    initGalleryNavigation(scope);
    initSmartSticky(scope);
    initDeliveryEstimators(scope);
    initTp(scope);
    initRelated(scope);
    initSp(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  window.addEventListener('pageshow', (event) => {
    const navigation = window.performance?.getEntriesByType?.('navigation')?.[0];
    if (!event.persisted && navigation?.type !== 'back_forward') return;
    window.requestAnimationFrame(() => {
      window.setTimeout(() => {
        document.querySelectorAll('[data-11p-tp]').forEach((root) => {
          root._elevenpRestoreConfiguration?.();
        });
      }, 0);
    });
  });

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      initialize(node);
    }));
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
